import { createIcons, Palette, Search, Save, Eye, Upload, RotateCcw, Send, History, Lock, CheckCircle2, AlertTriangle, X, Image as ImageIcon, LayoutTemplate, Type, LogIn, PanelsTopLeft, FileText, PenLine, LayoutDashboard, Fingerprint } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError } from "../../shared/errors/app-error.js";
import {
  FONT_OPTIONS,
  THEME_CATALOG,
  THEME_COLOR_KEYS,
  deriveThemeTokens,
  themeById,
  themeContrastIssues,
  type ThemeDesign,
  type ThemePalette,
  LOGO_SLOTS,
  OFFICIAL_SOFTWARE_NAME,
  STUDIO_TABS,
  TEMPLATED_DOCUMENTS,
  TEMPLATED_DOCUMENT_LABELS,
  TEMPLATE_STATUS_LABELS,
  HISTORY_ACTION_LABELS,
  studioEffectOf,
  type DocumentColors,
  type LogoSlot,
  type StudioSectionId,
  type TemplatedDocument,
  type VisualDesign
} from "../../domain/studio/visual-design.js";
import type { StudioView, LoginAppearance, StudioAssetTarget, PresetSummary, StudioOverviewItem, OverviewStatus, ThemeCardView } from "../../application/services/studio-design-service.js";
import { loadAndApplyAppearance } from "../studio/appearance.js";
import { IS_BACKEND_MODE } from "../../app/browser-platform.js";
import "./login-page.js";
import "../components/document/pdf-preview.js";

type Draft = Partial<VisualDesign>;

const HOME_CARDS: ReadonlyArray<{ tab: string; icon: string; title: string; subtitle: string; keywords: string }> = [
  { tab: "identidade", icon: "fingerprint", title: "Identidade INSPECT IA", subtitle: "Nome, subtítulo, slogan e versão", keywords: "nome software subtitulo slogan descricao versao marca" },
  { tab: "logos", icon: "image", title: "Logos", subtitle: "Principal, aplicação, documental, login, favicon", keywords: "logo favicon icone imagem marca" },
  { tab: "tema", icon: "palette", title: "Tema e Cores", subtitle: "Escuro, claro, automático e tokens", keywords: "tema cor cores escuro claro token fundo card borda sucesso erro atencao" },
  { tab: "tipografia", icon: "type", title: "Tipografia", subtitle: "Fontes, peso, escala e espaçamento", keywords: "fonte tipografia peso escala letra" },
  { tab: "login", icon: "log-in", title: "Tela de Login", subtitle: "Textos, fundo, card e botão Entrar", keywords: "login entrada fundo imagem card botao rodape" },
  { tab: "layout", icon: "panels-top-left", title: "Layout da Plataforma", subtitle: "Densidade, menu, cards, raio", keywords: "layout densidade menu largura card raio espacamento compacto confortavel" },
  { tab: "relatorios", icon: "file-text", title: "Relatórios", subtitle: "Logo documental, cores, QR", keywords: "relatorio documento pdf cabecalho tabela secao qr" },
  { tab: "templates", icon: "layout-template", title: "Templates", subtitle: "RI, ME, LP, PM, US, B-Scan, AVR, RDO", keywords: "template modelo ri me lp pm us bscan avr rdo" },
  { tab: "assinaturas", icon: "pen-line", title: "Assinaturas Visuais", subtitle: "Bloco de assinatura dos relatórios", keywords: "assinatura signatario registro funcao data" },
  { tab: "dashboard", icon: "layout-dashboard", title: "Dashboard", subtitle: "Cards, indicadores, Painel de Engenharia", keywords: "dashboard painel engenharia indicador card tabela" },
  { tab: "preview", icon: "eye", title: "Preview", subtitle: "Login, dashboard e relatórios antes de publicar", keywords: "preview visualizar previa pdf" },
  { tab: "historico", icon: "history", title: "Histórico Visual", subtitle: "Versões publicadas e restauração", keywords: "historico versao publicar restaurar rollback" }
];

/** Tipos cujo preview usa um documento real do sistema (mesmo serviço, mesmo builder que o PDF). */
const PREVIEW_DOCUMENT_TYPES: ReadonlyArray<{ id: string; label: string; serviceKey: string; template: TemplatedDocument }> = [
  { id: "ME", label: "ME", serviceKey: "meReportService", template: "ME" },
  { id: "LP", label: "LP", serviceKey: "lpReportService", template: "LP" },
  { id: "PM", label: "PM", serviceKey: "pmReportService", template: "PM" },
  { id: "USC", label: "US-Convencional", serviceKey: "conventionalUsReportService", template: "USC" },
  { id: "BSCAN", label: "B-Scan / C-Scan", serviceKey: "bscanReportService", template: "BSCAN" },
  { id: "AVR", label: "AVR", serviceKey: "avrReportService", template: "AVR" },
  // BOOK — o "documento" é o Equipamento (o Book é consolidado por Equipamento,
  // não tem um id próprio de relatório); `buildDocumentSnapshot(ctx, equipmentId)`
  // do consolidador segue a mesma assinatura dos demais para o preview genérico.
  { id: "BOOK", label: "Book de Inspeção", serviceKey: "inspectionBookConsolidatorService", template: "BOOK" }
];

const COLOR_LABELS: Record<keyof DocumentColors, string> = {
  headerText: "Texto do cabeçalho (logo e título)", sectionNumberFill: "Número da seção — fundo", sectionNumberText: "Número da seção — texto",
  sectionTitleFill: "Título da seção — fundo", sectionTitleText: "Título da seção — texto", tableHeaderFill: "Cabeçalho de tabela — fundo",
  tableHeaderText: "Cabeçalho de tabela — texto", border: "Bordas / linhas"
};

const THEME_LABELS: Record<keyof ThemePalette, string> = {
  primary: "Cor principal", accent: "Principal — hover", primaryActive: "Principal — pressionado", primaryText: "Texto sobre a cor principal",
  background: "Fundo", cards: "Card / superfície", secondary: "Superfície elevada / controle", surfaceStrong: "Superfície forte (cabeçalho de tabela)", inputBackground: "Fundo dos campos",
  textPrimary: "Texto principal", textSecondary: "Texto secundário", textMuted: "Texto atenuado (legenda)",
  borders: "Borda", borderStrong: "Borda forte", sidebar: "Menu lateral e topo",
  success: "Sucesso", warning: "Atenção", danger: "Erro (semântico)", info: "Informação"
};

/** Grupos do editor de tema — mesma ordem da paleta semântica. */
const THEME_GROUPS: ReadonlyArray<{ title: string; keys: ReadonlyArray<keyof ThemePalette> }> = [
  { title: "Cor institucional", keys: ["primary", "accent", "primaryActive", "primaryText"] },
  { title: "Superfícies", keys: ["background", "cards", "secondary", "surfaceStrong", "inputBackground", "sidebar"] },
  { title: "Texto e bordas", keys: ["textPrimary", "textSecondary", "textMuted", "borders", "borderStrong"] },
  { title: "Status (independentes da cor institucional)", keys: ["success", "warning", "danger", "info"] }
];

const OVERVIEW_STATUS: Record<OverviewStatus, { label: string; cls: string }> = {
  NAO_CONFIGURADO: { label: "NÃO CONFIGURADO", cls: "" },
  CONFIGURADO: { label: "CONFIGURADO", cls: "ok" },
  RASCUNHO: { label: "RASCUNHO", cls: "warn" },
  ATIVO: { label: "ATIVO", cls: "ok" },
  ERRO: { label: "ERRO", cls: "err" },
  SEM_PERMISSAO: { label: "SEM PERMISSÃO", cls: "lock" }
};

/**
 * STUDIO INSPECT IA — como o sistema APARECE. Toda edição vai para o
 * RASCUNHO; nada muda no sistema ativo até PUBLICAR. A tela é conveniência:
 * permissão, validação, versão e histórico são do `StudioDesignService`.
 */
class AppStudioDesignPage extends HTMLElement {
  private tab = "inicio";
  private search = "";
  private view: StudioView | null = null;
  private edits: Partial<Record<StudioSectionId, any>> = {};
  private loading = true;
  private loadError = "";
  private notice: { kind: "ok" | "error" | "info"; text: string } | null = null;
  private busy = "";
  private history: any[] | null = null;
  private modal: "publish" | "restore" | "login" | "lightbox" | "preset" | null = null;
  /** Temas salvos no sistema — nenhum aplicado sozinho. */
  private presets: PresetSummary[] = [];
  private presetTarget: PresetSummary | null = null;
  private publishReason = "";
  private restoreTarget = 0;
  private restoreReason = "";
  private restoreConfirm = "";
  private lightbox = { src: "", title: "" };
  private loginPreview: LoginAppearance | null = null;
  private previewTarget = "LOGIN";
  private previewSource: "DRAFT" | "PUBLISHED" = "DRAFT";
  /** O que o modal VISUALIZAR LOGIN está mostrando. */
  private loginModalSource: "DRAFT" | "PUBLISHED" = "DRAFT";
  private previewDocs: Array<{ id: string; label: string }> = [];
  private previewDocId = "";
  private previewMessage = "";
  /** Tema do catálogo em VISUALIZAR (só na tela, nada é gravado). */
  private themePreviewId: string | null = null;
  /** Depois de aplicar um tema pelo DEFINIR COMO ATIVO, abre a publicação. */
  private publishAfterApply = false;

  connectedCallback() {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("tab");
    if (requested && (requested === "inicio" || STUDIO_TABS.some(t => t.id === requested))) this.tab = requested;
    void this.load();
  }

  // ═══════════════════════════════════════════════════════ contexto

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private can(permission: string): boolean {
    const perms = contextStore.get().permissions as unknown as { has?: (p: string) => boolean } | string[] | undefined;
    if (!perms) return false;
    return typeof (perms as { has?: unknown }).has === "function" ? !!(perms as { has: (p: string) => boolean }).has(permission) : (perms as string[]).includes(permission);
  }

  private get svc(): any { return window.app.platform.studioDesignService; }

  private messageOf(e: unknown, fallback: string): string {
    if (e instanceof AppError) return (e as { safeMessage?: string }).safeMessage || e.message || fallback;
    return (e as { message?: string })?.message || fallback;
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
  }

  // ═══════════════════════════════════════════════════════ carga

  /**
   * Hidratação: primeiro o estado de carregamento, depois os dados GRAVADOS.
   * Nada é desenhado com padrões enquanto a resposta não chega, e uma falha
   * NÃO vira "valores padrão" na tela — vira o erro, com TENTAR NOVAMENTE.
   */
  private async load() {
    this.loading = true;
    this.loadError = "";
    this.view = null;
    this.render();
    try {
      this.setView(await this.svc.getStudio(this.ctx()));
    } catch (e) {
      console.error("[STUDIO] Falha ao carregar as configurações do Studio", e);
      this.loadError = this.messageOf(e, "Erro desconhecido.");
    }
    if (this.view) {
      try {
        this.presets = await this.svc.listPresets(this.ctx());
      } catch (e) {
        console.error("[STUDIO] Falha ao carregar os temas salvos", e);
        this.presets = [];
        this.notice = { kind: "error", text: `Os temas salvos não puderam ser carregados: ${this.messageOf(e, "erro desconhecido")}` };
      }
    }
    this.loading = false;
    if (this.view) await this.loadTabData();
    this.render();
  }

  private setView(view: StudioView) {
    this.view = view;
    this.edits = {};
    for (const s of view.readable) this.edits[s] = structuredClone((view.draft.design as Record<string, unknown>)[s]);
  }

  private async loadTabData() {
    try {
      if (this.tab === "historico" && this.can(PermissionCode.StudioAuditRead)) this.history = await this.svc.listHistory(this.ctx(), 300);
      if (this.tab === "preview" && this.previewTarget === "LOGIN") await this.refreshLoginPreview();
    } catch (e) {
      this.notice = { kind: "error", text: this.messageOf(e, "Não foi possível carregar esta aba.") };
    }
  }

  private async refreshLoginPreview() {
    this.loginPreview = await this.svc.getLoginPreview(this.ctx(), this.previewSource === "DRAFT" ? "DRAFT" : (this.view?.published.version ?? 0));
  }

  // ═══════════════════════════════════════════════════════ ações

  private editable(section: StudioSectionId): boolean { return !!this.view?.editable.includes(section); }

  private dirty(section: StudioSectionId): boolean {
    if (!this.view) return false;
    return JSON.stringify(this.edits[section]) !== JSON.stringify((this.view.draft.design as Record<string, unknown>)[section]);
  }

  private async act(run: () => Promise<string>, busy = "Processando…") {
    this.busy = busy;
    this.notice = null;
    this.render();
    try {
      const message = await run();
      if (message) this.notice = { kind: "ok", text: message };
      this.history = null;
    } catch (e) {
      this.notice = { kind: "error", text: this.messageOf(e, "Não foi possível concluir.") };
    }
    this.busy = "";
    await this.loadTabData();
    this.render();
  }

  private saveSection(section: StudioSectionId) {
    return this.act(async () => {
      this.setView(await this.svc.saveDraftSection(this.ctx(), section, this.edits[section], this.view!.draft.version));
      return "Salvo no RASCUNHO. O sistema ativo só muda quando alguém com permissão PUBLICAR.";
    }, "Salvando rascunho…");
  }

  private uploadFile(target: StudioAssetTarget, file: File) {
    return this.act(async () => {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      this.setView(await this.svc.uploadAsset(this.ctx(), target, dataUrl, file.name, this.view!.draft.version));
      return `${file.name} enviado ao rascunho.`;
    }, "Enviando arquivo…");
  }

  // ═══════════════════════════════════════════════════════ render

  private render() {
    const v = this.view;
    const tabs = STUDIO_TABS.filter(t => this.tabVisible(t.section));
    const q = this.norm(this.search);
    const filteredTabs = q ? tabs.filter(t => this.norm(t.label).includes(q) || HOME_CARDS.some(c => c.tab === t.id && this.norm(`${c.title} ${c.subtitle} ${c.keywords}`).includes(q))) : tabs;
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="sd-head">
        <div>
          <h1><i data-lucide="palette"></i> STUDIO INSPECT IA</h1>
          <p>Central de identidade, design e personalização visual da plataforma.</p>
          <p class="sd-meta">Define <b>como o sistema aparece</b>. Regras, numeração, segurança e integrações ficam em <b>Configurações</b>.
          ${v ? ` · Versão publicada: <b>V${v.published.version}</b>${v.published.version === 0 ? " (padrão INSPECT IA)" : ""}${v.versions[0] ? ` em ${this.esc(new Date(v.versions[0].publishedAt).toLocaleString("pt-BR"))} por ${this.esc(v.versions[0].publishedByName)}` : ""}` : ""}</p>
        </div>
        <div class="sd-head-actions">
          <div class="sd-search"><i data-lucide="search"></i><input id="sd-search" placeholder="Buscar elemento visual..." value="${this.esc(this.search)}"></div>
          ${v ? `<button class="sd-btn" id="sd-go-preview"><i data-lucide="eye"></i> VISUALIZAR</button>` : ""}
          ${v?.canPublish ? `<button class="sd-btn primary" id="sd-publish" ${v.pending.length === 0 ? "disabled" : ""}><i data-lucide="send"></i> PUBLICAR</button>` : ""}
        </div>
      </div>
      ${v && v.pending.length > 0 ? `<div class="sd-banner info"><b>Rascunho com alterações não publicadas:</b> ${v.pending.map(s => this.esc(this.sectionLabel(s))).join(", ")}. O sistema ativo continua na V${v.published.version} até PUBLICAR.</div>` : ""}
      ${v && !IS_BACKEND_MODE ? `<div class="sd-banner info">Teste local (sem servidor): o Studio grava neste navegador, como todo o modo local. No servidor, versões e arquivos ficam no banco e valem para todos.</div>` : ""}
      ${this.notice ? `<div class="sd-banner ${this.notice.kind}">${this.esc(this.notice.text)}</div>` : ""}
      ${this.loadError ? `<div class="sd-load-error" role="alert"><b>NÃO FOI POSSÍVEL CARREGAR AS CONFIGURAÇÕES.</b><span>${this.esc(this.loadError)}</span><span class="sd-note">Nenhum valor padrão está sendo mostrado como se estivesse salvo.</span><button class="sd-btn primary" id="sd-retry"><i data-lucide="rotate-ccw"></i> TENTAR NOVAMENTE</button></div>` : ""}
      <div class="sd-layout">
        <nav class="sd-nav">
          <button class="sd-nav-item ${this.tab === "inicio" ? "active" : ""}" data-tab="inicio"><span class="sd-num">00</span>Início</button>
          ${filteredTabs.map(t => `<button class="sd-nav-item ${t.id === this.tab ? "active" : ""}" data-tab="${t.id}"><span class="sd-num">${t.number}</span>${this.esc(t.label)}${v && this.tabPending(t.section) ? `<span class="sd-dot" title="Alteração não publicada"></span>` : ""}</button>`).join("")}
        </nav>
        <main class="sd-main">${this.loading ? `<div class="sd-empty">Carregando Studio…</div>` : v ? this.renderTab() : ""}${this.busy ? `<div class="sd-busy">${this.esc(this.busy)}</div>` : ""}</main>
      </div>
      ${this.renderModal()}`;
    createIcons({ icons: { Palette, Search, Save, Eye, Upload, RotateCcw, Send, History, Lock, CheckCircle2, AlertTriangle, X, Image: ImageIcon, LayoutTemplate, Type, LogIn, PanelsTopLeft, FileText, PenLine, LayoutDashboard, Fingerprint }, attrs: { width: "15", height: "15" } });
    this.bind();
    const lp = this.querySelector("app-login-page") as (HTMLElement & { previewAppearance?: LoginAppearance }) | null;
    if (lp && this.loginPreview) lp.previewAppearance = this.loginPreview;
  }

  private norm(s: string): string {
    return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  }

  private sectionLabel(s: StudioSectionId): string {
    return ({ identity: "Identidade", logos: "Logos", theme: "Tema e Cores", typography: "Tipografia", login: "Tela de Login", layout: "Layout", documents: "Relatórios / Cabeçalho e Rodapé", templates: "Templates", signature: "Assinaturas Visuais", dashboard: "Dashboard" } as Record<StudioSectionId, string>)[s];
  }

  private tabPending(section: string): boolean {
    const v = this.view!;
    if (section === "headerFooter") return v.pending.includes("documents");
    return v.pending.includes(section as StudioSectionId);
  }

  private tabVisible(section: string): boolean {
    if (section === "history") return this.can(PermissionCode.StudioAuditRead);
    if (section === "preview") return this.can(PermissionCode.StudioRead);
    if (section === "headerFooter") return !!this.view?.readable.includes("documents");
    if (!this.view) return false;
    return this.view.readable.includes(section as StudioSectionId);
  }

  private renderTab(): string {
    switch (this.tab) {
      case "inicio": return this.renderHome();
      case "identidade": return this.renderIdentity();
      case "logos": return this.renderLogos();
      case "tema": return this.renderTheme();
      case "tipografia": return this.renderTypography();
      case "login": return this.renderLogin();
      case "layout": return this.renderLayout();
      case "relatorios": return this.renderDocuments();
      case "cabecalho": return this.renderHeaderFooter();
      case "templates": return this.renderTemplates();
      case "assinaturas": return this.renderSignature();
      case "dashboard": return this.renderDashboard();
      case "preview": return this.renderPreview();
      case "historico": return this.renderHistory();
      default: return "";
    }
  }

  // ─────────────────────────────────────────────────────── comuns

  private effect(section: string, field: string): string {
    const e = studioEffectOf(section, field);
    if (!e) return "";
    return e.applied
      ? `<span class="sd-effect ok" title="${this.esc(e.where)}"><i data-lucide="check-circle-2"></i> APLICADO</span>`
      : `<span class="sd-effect reg" title="${this.esc(e.where)}"><i data-lucide="alert-triangle"></i> REGISTRADO — AINDA NÃO APLICADO</span>`;
  }

  private note(section: string, field: string): string {
    const e = studioEffectOf(section, field);
    return e ? `<small class="sd-note">${this.esc(e.where)}</small>` : "";
  }

  private head(icon: string, title: string, subtitle: string): string {
    return `<div class="sd-section-head"><h2><i data-lucide="${icon}"></i> ${this.esc(title)}</h2><p>${subtitle}</p></div>`;
  }

  private saveBar(section: StudioSectionId): string {
    // UX Correção: mensagem redundante removida; estado readonly já é tratado visualmente pelo container
if (!this.editable(section)) return '';
    const dirty = this.dirty(section);
    return `<div class="sd-savebar">
      <span class="sd-note">${dirty ? "Alterações nesta aba ainda não foram salvas no rascunho." : "Nada pendente nesta aba."}</span>
      <div class="sd-savebar-actions">
        <button class="sd-btn ghost" data-discard-edit="${section}" ${dirty ? "" : "disabled"}><i data-lucide="rotate-ccw"></i> Desfazer</button>
        <button class="sd-btn primary" data-save="${section}" ${dirty && !this.busy ? "" : "disabled"}><i data-lucide="save"></i> SALVAR RASCUNHO</button>
      </div>
    </div>`;
  }

  private ro(section: StudioSectionId): string { return this.editable(section) ? "" : "disabled"; }

  private get(section: StudioSectionId, path: string): any {
    let t: any = this.edits[section];
    for (const p of path.split(".")) t = t?.[p];
    return t;
  }

  private text(section: StudioSectionId, path: string, placeholder = "", maxLength = 300, multiline = false): string {
    const value = this.get(section, path) ?? "";
    return multiline
      ? `<textarea class="sd-ctl" rows="2" maxlength="${maxLength}" data-sec="${section}" data-path="${path}" data-kind="str" placeholder="${this.esc(placeholder)}" ${this.ro(section)}>${this.esc(value)}</textarea>`
      : `<input class="sd-ctl" maxlength="${maxLength}" data-sec="${section}" data-path="${path}" data-kind="str" value="${this.esc(value)}" placeholder="${this.esc(placeholder)}" ${this.ro(section)}>`;
  }

  private select(section: StudioSectionId, path: string, options: ReadonlyArray<{ value: string; label: string }>, kind = "str"): string {
    const current = String(this.get(section, path) ?? "");
    return `<select class="sd-ctl" data-sec="${section}" data-path="${path}" data-kind="${kind}" ${this.ro(section)}>${options.map(o => `<option value="${this.esc(o.value)}" ${o.value === current ? "selected" : ""}>${this.esc(o.label)}</option>`).join("")}</select>`;
  }

  private toggle(section: StudioSectionId, path: string, label: string, disabled = false): string {
    return `<label class="sd-toggle"><input type="checkbox" data-sec="${section}" data-path="${path}" data-kind="bool" ${this.get(section, path) ? "checked" : ""} ${disabled || !this.editable(section) ? "disabled" : ""}> ${this.esc(label)}</label>`;
  }

  /** Cor obrigatória (#RRGGBB): seletor + campo de texto. */
  private color(section: StudioSectionId, path: string, label: string): string {
    const v = String(this.get(section, path) ?? "#000000");
    return `<label class="sd-color"><span>${this.esc(label)}</span><span class="sd-color-row"><input type="color" value="${this.esc(v)}" data-sec="${section}" data-path="${path}" data-kind="color" ${this.ro(section)}><input class="sd-ctl sd-hex" value="${this.esc(v)}" data-sec="${section}" data-path="${path}" data-kind="color" maxlength="7" ${this.ro(section)}></span></label>`;
  }

  /** Cor opcional: vazia = a cor própria do modelo de relatório. */
  private optionalColor(section: StudioSectionId, path: string, label: string): string {
    const v = this.get(section, path) as string | null;
    return `<label class="sd-color"><span>${this.esc(label)}</span><span class="sd-color-row">
      <input type="checkbox" title="Personalizar" data-optcolor-toggle="${section}|${path}" ${v ? "checked" : ""} ${this.ro(section)}>
      ${v ? `<input type="color" value="${this.esc(v)}" data-sec="${section}" data-path="${path}" data-kind="color" ${this.ro(section)}><input class="sd-ctl sd-hex" value="${this.esc(v)}" data-sec="${section}" data-path="${path}" data-kind="color" maxlength="7" ${this.ro(section)}>` : `<span class="sd-note">cor do modelo (padrão)</span>`}
    </span></label>`;
  }

  private row(section: string, field: string, label: string, control: string): string {
    return `<div class="sd-row"><div class="sd-row-label"><span>${this.esc(label)}</span>${this.effect(section, field)}${this.note(section, field)}</div><div class="sd-row-control">${control}</div></div>`;
  }

  // ─────────────────────────────────────────────────────── temas salvos

  private renderPresets(compact = false): string {
    if (this.presets.length === 0) return "";
    return `
      <div class="sd-card-block">
        <div class="sd-card-title">Temas salvos</div>
        <p class="sd-note">Ficam guardados no sistema e <b>não são aplicados sozinhos</b>. APLICAR escreve no RASCUNHO; o sistema ativo só muda quando alguém PUBLICAR — e volta aplicando outro tema ou restaurando uma versão no Histórico.</p>
        <div class="sd-presets">${this.presets.map(pr => `
          <div class="sd-preset">
            ${pr.referenceImage && !compact ? `<img class="sd-preset-ref" src="${this.esc(pr.referenceImage)}" alt="Modelo ${this.esc(pr.name)}" data-lightbox="Modelo aprovado — ${this.esc(pr.name)}">` : ""}
            <div class="sd-preset-body">
              <div class="sd-preset-name">${this.esc(pr.name)}</div>
              <div class="sd-note">${this.esc(pr.description)}</div>
              ${compact ? "" : `<ul class="sd-preset-list">${pr.highlights.map(h => `<li>${this.esc(h)}</li>`).join("")}</ul>
              <div class="sd-note">Altera no rascunho: ${pr.sections.map(sec => this.esc(this.sectionLabel(sec))).join(", ")}.</div>
              ${pr.assets.length > 0 ? `<div class="sd-preset-assets">${[...new Map(pr.assets.map(a => [a.url, a])).values()].map(a => `<span class="sd-preset-asset"><img src="${this.esc(a.url)}" alt="${this.esc(a.label)}"></span>`).join("")}</div>` : ""}`}
              <div class="sd-inline">
                ${pr.canApply ? `<button class="sd-btn sm" data-apply-preset="${this.esc(pr.id)}"><i data-lucide="palette"></i> APLICAR AO RASCUNHO</button>` : `<span class="sd-note">Sem permissão de edição em todas as seções que este tema altera.</span>`}
              </div>
            </div>
          </div>`).join("")}</div>
      </div>`;
  }

  // ─────────────────────────────────────────────────────── início

  private renderHome(): string {
    const q = this.norm(this.search);
    const o = this.view!.overview;
    const cards = HOME_CARDS.filter(c => this.tabVisible(STUDIO_TABS.find(t => t.id === c.tab)?.section ?? "") && (!q || this.norm(`${c.title} ${c.subtitle} ${c.keywords}`).includes(q)));
    const statusOf: Record<string, StudioOverviewItem | undefined> = { identidade: o.identity, logos: o.logos, tema: o.theme, login: o.login, relatorios: o.documents, templates: o.templates };
    const item = (label: string, it: StudioOverviewItem, extra = "") => `
      <div class="sd-ov-item">
        <div class="sd-ov-label">${this.esc(label)}</div>
        <div class="sd-ov-value">${this.esc(it.value)}</div>
        <div class="sd-ov-tags">${this.statusTag(it.status)}${it.pending ? `<span class="sd-tag warn">RASCUNHO NÃO PUBLICADO</span>` : ""}</div>
        <div class="sd-note">${this.esc(it.detail)}</div>${extra}
      </div>`;
    const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
    return `
      <div class="sd-card-block">
        <div class="sd-card-title">Estado gravado do Studio</div>
        <p class="sd-note">Tudo abaixo vem da versão <b>publicada</b> (o que o sistema usa agora) e do <b>rascunho</b> gravado — nunca de padrões tomados como configuração.</p>
        <div class="sd-ov-grid">
          ${item("Identidade", o.identity)}
          ${item("Tema ativo", o.theme, o.draftTheme ? `<div class="sd-note">Rascunho: <b>${this.esc(o.draftTheme.name)}${o.draftTheme.customized ? " (personalizado)" : ""}</b> — em edição, ainda não publicado.</div>` : "")}
          <div class="sd-ov-item">
            <div class="sd-ov-label">Versão publicada</div>
            <div class="sd-ov-value">V${o.publishedVersion}${o.publishedVersion === 0 ? " — padrão de fábrica" : ""}</div>
            <div class="sd-ov-tags">${this.statusTag("ATIVO")}</div>
            <div class="sd-note">${o.lastPublication ? `Última publicação: ${this.esc(when(o.lastPublication.at))} por ${this.esc(o.lastPublication.by)}` : "Nenhuma publicação ainda: vale a identidade de fábrica INSPECT IA."}</div>
          </div>
          <div class="sd-ov-item">
            <div class="sd-ov-label">Rascunho</div>
            <div class="sd-ov-value">${o.draft.pendingSections.length === 0 ? "Sem pendências" : `${o.draft.pendingSections.length} área(s) não publicada(s)`}</div>
            <div class="sd-ov-tags">${o.draft.pendingSections.length > 0 ? this.statusTag("RASCUNHO") : ""}</div>
            <div class="sd-note">${o.draft.pendingSections.length > 0 ? `${o.draft.pendingSections.map(sec => this.esc(this.sectionLabel(sec))).join(", ")} · gravado em ${this.esc(when(o.draft.updatedAt))}${o.draft.updatedBy ? ` por ${this.esc(o.draft.updatedBy)}` : ""}` : "O rascunho é igual à versão publicada."}</div>
          </div>
          ${item("Login", o.login)}
          ${item("Relatórios", o.documents)}
          ${item("Templates", o.templates)}
          ${item("Logos", o.logos)}
        </div>
      </div>
      <div class="sd-cards">${cards.length === 0 ? `<div class="sd-empty">Nenhum elemento visual encontrado para "${this.esc(this.search)}".</div>` : cards.map(c => {
        const section = STUDIO_TABS.find(t => t.id === c.tab)?.section ?? "";
        const st = statusOf[c.tab];
        return `<button class="sd-card" data-tab="${c.tab}"><span class="sd-card-icon"><i data-lucide="${c.icon}"></i></span><span class="sd-card-title">${this.esc(c.title)}</span><span class="sd-card-sub">${this.esc(st ? st.value : c.subtitle)}</span><span class="sd-ov-tags">${st ? this.statusTag(st.status) : ""}${this.view && this.tabPending(section) ? `<span class="sd-tag warn">NÃO PUBLICADO</span>` : ""}</span></button>`;
      }).join("")}</div>
      ${this.renderPresets(true)}`;
  }

  private statusTag(status: OverviewStatus): string {
    const st = OVERVIEW_STATUS[status];
    return `<span class="sd-tag ${st.cls}">${st.label}</span>`;
  }

  // ─────────────────────────────────────────────────────── 01 identidade

  private renderIdentity(): string {
    const s: StudioSectionId = "identity";
    return `
      ${this.head("fingerprint", "01 — Identidade INSPECT IA", "Somente elementos da identidade INSPECT IA. Não existe campo de empresa licenciadora.")}
      <div class="sd-card-block">
        <div class="sd-row"><div class="sd-row-label"><span>Nome do software</span><small class="sd-note">Fixo — identidade oficial.</small></div><div class="sd-row-control"><input class="sd-ctl" value="${OFFICIAL_SOFTWARE_NAME}" disabled> <span class="sd-tag lock"><i data-lucide="lock"></i> TRAVADO</span></div></div>
        ${this.row(s, "subtitle", "Subtítulo institucional", this.text(s, "subtitle", "SISTEMA DE INSPEÇÃO E INTEGRIDADE MECÂNICA", 120))}
        ${this.row(s, "slogan", "Slogan", this.text(s, "slogan", "", 120))}
        ${this.row(s, "description", "Descrição institucional", this.text(s, "description", "", 400, true))}
        ${this.row(s, "versionLabel", "Identificação da versão", this.text(s, "versionLabel", "V-11", 30))}
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 02 logos

  private renderLogos(): string {
    const s: StudioSectionId = "logos";
    const logos = (this.edits.logos ?? {}) as VisualDesign["logos"];
    return `
      ${this.head("image", "02 — Logos", "Todos dentro da identidade INSPECT IA. Sem arquivo enviado, vale o padrão (o texto INSPECT IA). Os arquivos vão para o armazenamento oficial — a configuração guarda só a referência.")}
      <div class="sd-logo-grid">${LOGO_SLOTS.map(slot => {
        const asset = logos[slot.slot];
        const url = asset ? this.view!.assetUrls[asset.ref] : null;
        return `
          <div class="sd-logo">
            <div class="sd-logo-title">${this.esc(slot.label)} ${this.effect("logos", slot.slot)}</div>
            <div class="sd-logo-box">${url ? `<img src="${this.esc(url)}" alt="${this.esc(slot.label)}" data-lightbox="${this.esc(slot.label)}">` : `<span class="sd-wordmark">INSPECT <b>IA</b></span>`}</div>
            <small class="sd-note">${this.esc(slot.note)} ${slot.mimeTypes.map(m => m.replace("image/", "").toUpperCase()).join("/")} · até ${(slot.maxBytes / 1024).toFixed(0)} KB</small>
            ${asset ? `<small class="sd-note">${this.esc(asset.fileName)} · ${(asset.size / 1024).toFixed(0)} KB</small>` : `<small class="sd-note">Padrão INSPECT IA</small>`}
            <div class="sd-inline">
              ${this.editable(s) ? `<button class="sd-btn sm" data-upload-logo="${slot.slot}"><i data-lucide="upload"></i> ALTERAR</button>` : ""}
              ${url ? `<button class="sd-btn sm ghost" data-view-src="${this.esc(url)}" data-view-title="${this.esc(slot.label)}"><i data-lucide="eye"></i> VISUALIZAR PRÉVIA</button>` : ""}
              ${this.editable(s) && asset ? `<button class="sd-btn sm ghost" data-clear-logo="${slot.slot}"><i data-lucide="rotate-ccw"></i> RESTAURAR PADRÃO</button>` : ""}
            </div>
            <input type="file" accept="${slot.mimeTypes.join(",")}" data-logo-input="${slot.slot}" hidden>
          </div>`;
      }).join("")}</div>`;
  }

  // ─────────────────────────────────────────────────────── 03 tema

  private renderTheme(): string {
    const s: StudioSectionId = "theme";
    const t = this.edits.theme as ThemeDesign;
    const previewEntry = this.themePreviewId ? themeById(this.themePreviewId) : null;
    const previewTheme: ThemeDesign = previewEntry ? previewEntry.theme : t;
    const origin = themeById(t.themeId);
    const localIssues = themeContrastIssues(t);
    return `
      ${this.head("palette", "03 — Tema e Cores", "Os temas mudam SÓ as cores da aplicação. Aplicar escreve no RASCUNHO; o sistema ativo muda ao PUBLICAR. PDFs aprovados guardam o visual da época.")}
      <div class="sd-theme-cards">${this.view!.themes.map(card => this.themeCard(card)).join("")}</div>
      ${this.renderPresets()}

      <div class="sd-card-block">
        <div class="sd-card-title">Editor do tema no rascunho: ${this.esc(origin?.name ?? "tema desconhecido")}${this.dirty(s) ? `<span class="sd-tag warn">ALTERAÇÃO LOCAL NÃO SALVA</span>` : ""}</div>
        <p class="sd-note">Cada cor aceita o seletor ou o código HEX (#RRGGBB). Valor inválido não é aceito. ↺ volta a cor do tema de origem. O preview abaixo reage na hora; nada é publicado.</p>
        ${this.row(s, "mode", "Modo", this.select(s, "mode", [{ value: "ESCURO", label: "Escuro" }, { value: "CLARO", label: "Claro" }, { value: "AUTOMATICO", label: "Automático (segue o sistema operacional)" }]))}
        ${THEME_GROUPS.map(g => `
          <div class="sd-theme-group">
            <div class="sd-theme-group-title">${this.esc(g.title)}</div>
            <div class="sd-color-grid">${g.keys.map(k => this.themeColor(k, origin?.theme[k] ?? null)).join("")}</div>
          </div>`).join("")}
        ${localIssues.length > 0 ? `<div class="sd-banner error"><b>Contraste insuficiente:</b><ul>${localIssues.map(i => `<li>${this.esc(i)}</li>`).join("")}</ul></div>` : `<div class="sd-warnline ok"><i data-lucide="check-circle-2"></i> Todos os pares de texto e fundo passam no contraste mínimo (WCAG AA).</div>`}
      </div>

      <div class="sd-card-block">
        <div class="sd-card-title">Preview ${previewEntry ? `— ${this.esc(previewEntry.name)} (só visualização)` : "— cores do rascunho"}${previewEntry ? `<button class="sd-btn sm ghost" id="sd-theme-preview-close"><i data-lucide="x"></i> Voltar ao rascunho</button>` : ""}</div>
        ${this.themePreview(previewTheme)}
      </div>
      ${this.saveBar(s)}`;
  }

  private themeCard(card: ThemeCardView): string {
    const p = card.palette;
    const statusLabel = card.status === "ATIVO" ? "ATIVO" : card.status === "RASCUNHO" ? "RASCUNHO" : "DISPONÍVEL";
    const cls = card.status === "ATIVO" ? "ok" : card.status === "RASCUNHO" ? "warn" : "";
    const when = card.changedAt ? new Date(card.changedAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
    const canEdit = this.editable("theme");
    const inDraft = (this.view!.draft.design.theme as ThemeDesign | undefined)?.themeId === card.id;
    return `
      <div class="sd-theme-card ${card.status === "ATIVO" ? "is-active" : ""}">
        <div class="sd-theme-mini" style="background:${this.esc(p.background)}">
          <span class="sd-mini-side" style="background:${this.esc(p.sidebar)}"></span>
          <span class="sd-mini-body">
            <span class="sd-mini-card" style="background:${this.esc(p.cards)};border-color:${this.esc(p.borders)}">
              <span style="background:${this.esc(p.textPrimary)}"></span><span style="background:${this.esc(p.textSecondary)}"></span>
              <span class="sd-mini-btn" style="background:${this.esc(p.primary)}"></span>
            </span>
          </span>
        </div>
        <div class="sd-theme-card-body">
          <div class="sd-theme-name">${this.esc(card.name)}</div>
          <div class="sd-swatches">${(["primary", "sidebar", "textPrimary", "borders", "background", "cards", "success", "warning", "danger", "info"] as const).map(k => `<span title="${this.esc(THEME_LABELS[k])} ${this.esc(p[k])}" style="background:${this.esc(p[k])}"></span>`).join("")}</div>
          <div class="sd-ov-tags"><span class="sd-tag ${cls}">${statusLabel}</span>${card.customized ? `<span class="sd-tag">PERSONALIZADO</span>` : ""}${card.draftPending ? `<span class="sd-tag warn">ALTERAÇÃO NO RASCUNHO</span>` : ""}${card.contrastIssues.length > 0 ? `<span class="sd-tag err" title="${this.esc(card.contrastIssues.join(" "))}">CONTRASTE</span>` : ""}</div>
          <div class="sd-note">${this.esc(card.description)}</div>
          <div class="sd-note">Versão: <b>${card.version !== null ? `V${card.version}` : card.status === "RASCUNHO" ? "em edição" : "—"}</b> · Última alteração: <b>${this.esc(when)}</b>${card.changedBy ? ` por ${this.esc(card.changedBy)}` : ""}</div>
          <div class="sd-inline">
            <button class="sd-btn sm ghost" data-theme-preview="${this.esc(card.id)}"><i data-lucide="eye"></i> VISUALIZAR</button>
            ${canEdit && !inDraft ? `<button class="sd-btn sm" data-theme-apply="${this.esc(card.id)}"><i data-lucide="palette"></i> USAR NO RASCUNHO</button>` : ""}
            ${canEdit && inDraft ? `<button class="sd-btn sm" data-theme-edit="${this.esc(card.id)}"><i data-lucide="pen-line"></i> EDITAR</button>` : ""}
            ${canEdit && this.view!.canPublish && card.status !== "ATIVO" ? `<button class="sd-btn sm primary" data-theme-activate="${this.esc(card.id)}"><i data-lucide="send"></i> DEFINIR COMO TEMA ATIVO</button>` : ""}
          </div>
        </div>
      </div>`;
  }

  /** Cor do tema com seletor, HEX e "restaurar a do tema de origem". */
  private themeColor(key: keyof ThemePalette, originValue: string | null): string {
    const s: StudioSectionId = "theme";
    const v = String(this.get(s, key) ?? "#000000");
    const differs = originValue !== null && originValue.toUpperCase() !== v.toUpperCase();
    return `<label class="sd-color"><span>${this.esc(THEME_LABELS[key])}</span><span class="sd-color-row">
      <input type="color" value="${this.esc(v)}" data-sec="theme" data-path="${key}" data-kind="color" ${this.ro(s)} aria-label="${this.esc(THEME_LABELS[key])}">
      <input class="sd-ctl sd-hex" value="${this.esc(v)}" data-sec="theme" data-path="${key}" data-kind="color" maxlength="7" ${this.ro(s)} aria-label="${this.esc(THEME_LABELS[key])} (HEX)">
      ${this.editable(s) && differs ? `<button type="button" class="sd-btn sm ghost" data-theme-reset="${key}" title="Voltar para ${this.esc(originValue!)}">↺</button>` : ""}
    </span></label>`;
  }

  /**
   * PREVIEW EM TEMPO REAL — usa os MESMOS tokens que a aplicação recebe
   * (deriveThemeTokens), aplicados só dentro desta moldura. Menu e topo como
   * no sistema: escuros, com texto claro.
   */
  private themePreview(theme: ThemeDesign): string {
    const tokens = deriveThemeTokens(theme);
    const style = Object.entries(tokens).map(([k, v]) => `${k}:${v}`).join(";");
    return `
      <div class="sd-tp" style="${this.esc(style)}">
        <aside class="sd-tp-side"><b>INSPECT IA</b><span class="on">Visão Geral</span><span>Equipamentos</span><span>Inspeções</span><span>Relatórios</span></aside>
        <div class="sd-tp-main">
          <header class="sd-tp-head"><span>Painel de Indicadores</span><span class="sd-tp-user">Administrador</span></header>
          <div class="sd-tp-content">
            <div class="sd-tp-kpis">
              <div class="sd-tp-card"><small>ATIVOS NO ESCOPO</small><strong>128</strong><span>Total cadastrado</span></div>
              <div class="sd-tp-card"><small>AVANÇO REAL</small><strong>64%</strong><span>Medido</span></div>
              <div class="sd-tp-card"><small>RECOMENDAÇÕES</small><strong>7</strong><span>Pendentes</span></div>
            </div>
            <div class="sd-tp-card sd-tp-form">
              <label>Projeto<select><option>PRJ-0001 — Parada U-33</option></select></label>
              <label>Busca<input placeholder="Digite a TAG..." value="V-001"></label>
              <div class="sd-tp-actions"><button class="p">Aplicar filtros</button><button class="g">Limpar</button><button class="d">Excluir</button></div>
            </div>
            <table class="sd-tp-table"><thead><tr><th>TAG</th><th>Sistema</th><th>Status</th></tr></thead>
              <tbody><tr><td>V-001</td><td>SYS-A</td><td><span class="b s">APROVADO</span></td></tr><tr><td>T-100</td><td>SYS-B</td><td><span class="b w">ATENÇÃO</span></td></tr><tr><td>P-220</td><td>SYS-B</td><td><span class="b e">REPROVADO</span></td></tr><tr><td>L-015</td><td>SYS-C</td><td><span class="b i">EM ANÁLISE</span></td></tr></tbody></table>
            <div class="sd-tp-alert">Relatório enviado ao PLH. Aguardando análise.</div>
            <div class="sd-tp-modal"><div class="sd-tp-modal-head">Confirmar publicação</div><p>Texto do modal com <span class="muted">legenda atenuada</span>.</p><div class="sd-tp-actions"><button class="g">Cancelar</button><button class="p">Publicar</button></div></div>
          </div>
        </div>
      </div>`;
  }

  // ─────────────────────────────────────────────────────── 04 tipografia

  private renderTypography(): string {
    const s: StudioSectionId = "typography";
    const t = this.edits.typography as VisualDesign["typography"];
    const fonts = FONT_OPTIONS.map(f => ({ value: f.id, label: f.label }));
    const stack = (id: string) => FONT_OPTIONS.find(f => f.id === id)?.stack ?? "";
    return `
      ${this.head("type", "04 — Tipografia", "Só fontes oficiais disponíveis no projeto — não há envio de fonte. As fontes dependem de estarem instaladas no computador de quem usa; na falta, vale a próxima da lista.")}
      <div class="sd-card-block">
        ${this.row(s, "*", "Fonte principal", this.select(s, "bodyFont", fonts))}
        ${this.row(s, "*", "Fonte de títulos", this.select(s, "headingFont", fonts))}
        ${this.row(s, "*", "Peso padrão", this.select(s, "baseWeight", [{ value: "400", label: "400 — Regular" }, { value: "500", label: "500 — Médio" }], "int"))}
        ${this.row(s, "*", "Escala tipográfica", this.select(s, "scale", [{ value: "COMPACTA", label: "Compacta" }, { value: "PADRAO", label: "Padrão" }, { value: "AMPLA", label: "Ampla" }]))}
        ${this.row(s, "*", "Espaçamento de letras", this.select(s, "letterSpacing", [{ value: "NORMAL", label: "Normal" }, { value: "AMPLO", label: "Amplo" }]))}
      </div>
      <div class="sd-card-block"><div class="sd-card-title">Amostra</div>
        <div style="font-family:${this.esc(stack(t.headingFont))};font-size:1.3rem;font-weight:600">Relatório de Integridade — Sistema U-33</div>
        <div style="font-family:${this.esc(stack(t.bodyFont))};font-weight:${t.baseWeight};letter-spacing:${t.letterSpacing === "AMPLO" ? "0.01em" : "normal"}">Inspeção, relatórios, gestão da integridade e controle técnico. 0123456789</div>
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 05 login

  private renderLogin(): string {
    const s: StudioSectionId = "login";
    const l = this.edits.login as VisualDesign["login"];
    const bgUrl = l.backgroundImage ? this.view!.assetUrls[l.backgroundImage.ref] : null;
    return `
      ${this.head("log-in", "05 — Tela de Login", "Identidade INSPECT IA, tema escuro técnico, ambiente industrial — sem marca externa. Fluxo: Editar → VISUALIZAR LOGIN → SALVAR RASCUNHO → PUBLICAR.")}
      <div class="sd-card-block">
        ${this.row(s, "*", "Variante", this.select(s, "variant", [{ value: "ESCURO", label: "Escura — técnica (padrão INSPECT IA)" }, { value: "CLARO", label: "Clara — fundo cinza claro, texto grafite" }]))}
        ${this.row(s, "*", "Título", this.text(s, "title", "INSPECT IA", 60))}
        ${this.row(s, "*", "Subtítulo", this.text(s, "subtitle", "", 120))}
        ${this.row(s, "*", "Slogan (use Enter para quebrar a linha)", this.text(s, "slogan", "", 120, true))}
        ${this.row(s, "*", "Texto institucional", this.text(s, "institutionalText", "", 300, true))}
        ${this.row(s, "*", "Rodapé", this.text(s, "footerText", "", 160))}
        ${this.row(s, "*", "Subtítulo do card", this.text(s, "cardSubtitle", "", 120))}
        ${this.row(s, "*", "Lema do card", this.text(s, "cardMotto", "", 160, true))}
        ${this.row(s, "*", "Botão Entrar", this.text(s, "buttonLabel", "Entrar no Sistema", 40))}
        ${this.row(s, "*", "Posição do card", this.select(s, "cardPosition", [{ value: "DIREITA", label: "Direita (padrão)" }, { value: "ESQUERDA", label: "Esquerda" }, { value: "CENTRO", label: "Centro, sobre a imagem" }]))}
        ${this.row(s, "*", "Transparência da imagem (escurecimento)", `<input type="range" min="0" max="1" step="0.05" value="${l.overlayOpacity}" data-sec="${s}" data-path="overlayOpacity" data-kind="float" ${this.ro(s)}> <span class="sd-note">${Math.round(l.overlayOpacity * 100)}%</span>`)}
        <div class="sd-color-grid">
          ${this.color(s, "backgroundColor", "Cor de fundo")}
          ${this.color(s, "accentColor", "Cor de destaque")}
          <label class="sd-color"><span>Cor do botão Entrar</span><span class="sd-color-row"><input type="checkbox" data-optcolor-toggle="login|buttonColor" ${l.buttonColor ? "checked" : ""} ${this.ro(s)}>${l.buttonColor ? `<input type="color" value="${this.esc(l.buttonColor)}" data-sec="login" data-path="buttonColor" data-kind="color" ${this.ro(s)}>` : `<span class="sd-note">degradê padrão</span>`}</span></label>
        </div>
        <div class="sd-row"><div class="sd-row-label"><span>Imagem de fundo</span>${this.effect("login", "*")}</div><div class="sd-row-control">
          ${bgUrl ? `<img class="sd-bg-thumb" src="${this.esc(bgUrl)}" alt="Imagem de fundo" data-lightbox="Imagem de fundo">` : `<span class="sd-note">Padrão: ambiente industrial INSPECT IA.</span>`}
          <div class="sd-inline">
            ${this.can(PermissionCode.StudioLoginUpdate) ? `<button class="sd-btn sm" id="sd-bg-upload"><i data-lucide="upload"></i> ALTERAR</button>${l.backgroundImage ? `<button class="sd-btn sm ghost" id="sd-bg-clear"><i data-lucide="rotate-ccw"></i> RESTAURAR PADRÃO</button>` : ""}` : ""}
          </div>
          <input type="file" id="sd-bg-input" accept="image/png,image/jpeg,image/webp" hidden>
        </div></div>
        <div class="sd-row"><div class="sd-row-label"><span>Logo da marca (acima de INSPECT IA)</span><small class="sd-note">Aparece no painel institucional e no card de acesso.</small></div><div class="sd-row-control">
          ${l.brandLogo && this.view!.assetUrls[l.brandLogo.ref] ? `<img class="sd-bg-thumb" src="${this.esc(this.view!.assetUrls[l.brandLogo.ref]!)}" alt="Logo da marca" data-lightbox="Logo da marca no login">` : `<span class="sd-note">Nenhum — só o título.</span>`}
          <div class="sd-inline">
            ${this.can(PermissionCode.StudioLoginUpdate) ? `<button class="sd-btn sm" id="sd-brand-upload"><i data-lucide="upload"></i> ALTERAR</button>${l.brandLogo ? `<button class="sd-btn sm ghost" id="sd-brand-clear"><i data-lucide="rotate-ccw"></i> REMOVER</button>` : ""}` : ""}
          </div>
          <input type="file" id="sd-brand-input" accept="image/png,image/jpeg,image/webp" hidden>
        </div></div>
        <p class="sd-note">Ícone do card: aba Logos → "Logo da tela de login". Destaque: escreva <code>*texto*</code> para pintá-lo na cor de destaque (ex.: Plataforma licenciada pela *MARCA*).</p>
      </div>
      <div class="sd-card-block sd-login-status">
        <div class="sd-ov-item">
          <div class="sd-ov-label">Publicado — é o que o /login mostra</div>
          <div class="sd-ov-value">V${this.view!.published.version}${this.view!.published.version === 0 ? " — login de fábrica" : ""}</div>
          <div class="sd-ov-tags"><span class="sd-tag ok">ATIVO</span></div>
          <div class="sd-note">${this.view!.overview.lastPublication ? `Publicado em ${this.esc(new Date(this.view!.overview.lastPublication.at).toLocaleString("pt-BR"))} por ${this.esc(this.view!.overview.lastPublication.by)}` : "Nenhuma publicação: o /login usa o desenho de fábrica INSPECT IA."}</div>
        </div>
        <div class="sd-ov-item">
          <div class="sd-ov-label">Rascunho</div>
          <div class="sd-ov-value">${this.tabPending("login") ? "Alterações não publicadas" : "Igual ao publicado"}</div>
          <div class="sd-ov-tags">${this.tabPending("login") ? `<span class="sd-tag warn">NÃO PUBLICADO</span>` : ""}${this.dirty(s) ? `<span class="sd-tag warn">ALTERAÇÃO LOCAL NÃO SALVA</span>` : ""}</div>
          <div class="sd-note">O rascunho só aparece no /login depois de PUBLICAR.</div>
        </div>
      </div>
      <div class="sd-savebar"><span class="sd-note">Salve o rascunho antes de visualizar — o preview mostra o rascunho GRAVADO.</span><div class="sd-savebar-actions">
        <button class="sd-btn" id="sd-login-preview" ${this.dirty(s) ? "disabled title=\"Salve o rascunho primeiro\"" : ""}><i data-lucide="eye"></i> VISUALIZAR RASCUNHO</button>
        <button class="sd-btn ghost" id="sd-login-preview-published"><i data-lucide="eye"></i> VISUALIZAR PUBLICADO</button>
      </div></div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 06 layout

  private renderLayout(): string {
    const s: StudioSectionId = "layout";
    return `
      ${this.head("panels-top-left", "06 — Layout da Plataforma", "Só opções visuais seguras: nenhuma altera estrutura, menu, rotas ou fluxo.")}
      <div class="sd-card-block">
        ${this.row(s, "density", "Densidade", this.select(s, "density", [{ value: "COMPACTO", label: "Compacto" }, { value: "PADRAO", label: "Padrão" }, { value: "CONFORTAVEL", label: "Confortável" }]))}
        ${this.row(s, "sidebarWidth", "Largura do menu (px)", `<input class="sd-ctl" type="number" min="220" max="320" data-sec="${s}" data-path="sidebarWidth" data-kind="int" value="${this.get(s, "sidebarWidth")}" ${this.ro(s)}>`)}
        ${this.row(s, "cardStyle", "Estilo de cards", this.select(s, "cardStyle", [{ value: "CONTORNO", label: "Contorno (padrão)" }, { value: "PLANO", label: "Plano" }, { value: "ELEVADO", label: "Elevado" }]))}
        ${this.row(s, "radius", "Raio de borda (px)", `<input class="sd-ctl" type="number" min="0" max="12" data-sec="${s}" data-path="radius" data-kind="int" value="${this.get(s, "radius")}" ${this.ro(s)}>`)}
        ${this.row(s, "spacing", "Espaçamento", this.select(s, "spacing", [{ value: "JUSTO", label: "Justo" }, { value: "NORMAL", label: "Normal" }, { value: "AMPLO", label: "Amplo" }]))}
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 07 relatórios

  private renderDocuments(): string {
    const s: StudioSectionId = "documents";
    return `
      ${this.head("file-text", "07 — Relatórios", "SOMENTE a aparência documental. Se o QR é obrigatório ou se o documento exige assinatura digital é regra de Configurações. Padrão: INSPECT IA no cabeçalho, fundo branco, grafite, cinza, preto e bordas finas.")}
      <div class="sd-card-block">
        ${this.row(s, "logoMode", "Logo documental", this.select(s, "logoMode", [{ value: "TEXTO", label: "Texto INSPECT IA (padrão)" }, { value: "IMAGEM", label: "Imagem — Logo documental (aba Logos)" }]))}
        ${this.row(s, "qrPosition", "Posição do QR Code", this.select(s, "qrPosition", [{ value: "CENTRO", label: "Centro do cabeçalho (padrão)" }, { value: "DIREITA", label: "Canto superior direito" }]))}
      </div>
      <div class="sd-card-block">
        <div class="sd-card-title">Cores ${this.effect("documents", "colors")}</div>
        <p class="sd-note">Desmarcado = a cor própria de cada modelo (o padrão vigente). Documentos já aprovados não mudam: guardam o visual da época.</p>
        <div class="sd-color-grid">${(Object.keys(COLOR_LABELS) as Array<keyof DocumentColors>).map(k => this.optionalColor(s, `colors.${k}`, COLOR_LABELS[k])).join("")}</div>
      </div>
      ${this.view!.warnings.filter(w => w.startsWith("Relatórios")).map(w => `<div class="sd-warnline"><i data-lucide="alert-triangle"></i> ${this.esc(w)}</div>`).join("")}
      <div class="sd-savebar"><span class="sd-note">Para ver o resultado num documento real: aba Preview.</span><div class="sd-savebar-actions"><button class="sd-btn" data-tab="preview"><i data-lucide="eye"></i> VISUALIZAR RELATÓRIO</button></div></div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 08 cabeçalho e rodapé

  private renderHeaderFooter(): string {
    const s: StudioSectionId = "documents";
    return `
      ${this.head("file-text", "08 — Cabeçalhos e Rodapés", "O Studio controla posição e estilo. A ORIGEM dos dados (número, revisão, datas, Cliente, Projeto, Unidade, TAG) não muda.")}
      <div class="sd-card-block">
        <div class="sd-card-title">Cabeçalho</div>
        <div class="sd-header-mock">
          <div><b style="color:${this.esc(this.get(s, "colors.headerText") ?? "#002B49")}">INSPECT IA</b><br><small>SISTEMA DE INSPEÇÃO E INTEGRIDADE MECÂNICA</small><br><b style="color:${this.esc(this.get(s, "colors.headerText") ?? "#002B49")}">TÍTULO DO RELATÓRIO</b></div>
          ${this.get(s, "qrPosition") === "DIREITA" ? `<div class="sd-mock-fields">Nº · Revisão · Data de emissão · Período · OS</div><div class="sd-mock-qr">QR</div>` : `<div class="sd-mock-qr">QR</div><div class="sd-mock-fields">Nº · Revisão · Data de emissão · Período · OS</div>`}
        </div>
        ${this.row(s, "qrPosition", "Posição do QR Code", this.select(s, "qrPosition", [{ value: "CENTRO", label: "Entre o título e os dados (padrão)" }, { value: "DIREITA", label: "Canto superior direito" }]))}
        ${this.row(s, "colors.headerText", "Cor do logo e título", this.optionalColor(s, "colors.headerText", "Texto do cabeçalho"))}
        <p class="sd-note">Elementos do cabeçalho: logo INSPECT IA, número do relatório, revisão, data de emissão, período da inspeção, QR Code, Cliente/Projeto, Unidade/Área e TAG — cada relatório imprime os que o seu modelo tem; o Studio não acrescenta nem retira dado.</p>
      </div>
      <div class="sd-card-block">
        <div class="sd-card-title">Rodapé ${this.effect("documents", "footerInstitutionalText")}</div>
        ${this.row(s, "footerShowReportType", "Tipo do relatório no rodapé", this.toggle(s, "footerShowReportType", "Mostrar \"INSPECT IA | tipo | formulário\""))}
        ${this.row(s, "footerInstitutionalText", "Texto institucional (linha extra)", this.text(s, "footerInstitutionalText", "vazio = rodapé padrão", 160))}
        <div class="sd-row"><div class="sd-row-label"><span>Paginação</span></div><div class="sd-row-control"><span class="sd-tag lock"><i data-lucide="lock"></i> Página X de Y — sempre</span></div></div>
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 09 templates

  private renderTemplates(): string {
    const s: StudioSectionId = "templates";
    const tpls = this.edits.templates as VisualDesign["templates"];
    return `
      ${this.head("layout-template", "09 — Templates", "Estrutura VISUAL por tipo de documento, sobre o padrão de Relatórios. Não altera workflow, permissões, regras técnicas, status nem cálculos. Só o template PUBLICADO é aplicado aos documentos; EM EDIÇÃO impede a publicação.")}
      ${this.effect("templates", "*")}
      ${TEMPLATED_DOCUMENTS.map(t => {
        const tpl = tpls[t];
        return `
        <div class="sd-card-block">
          <div class="sd-card-title">${this.esc(TEMPLATED_DOCUMENT_LABELS[t])} (${t}) <span class="sd-tag ${tpl.status === "PUBLICADO" ? "ok" : tpl.status === "EM_EDICAO" ? "warn" : ""}">${this.esc(TEMPLATE_STATUS_LABELS[tpl.status])}</span>${t === "RI" || t === "RDO" ? `<span class="sd-effect reg"><i data-lucide="alert-triangle"></i> REGISTRADO — o builder do ${t} ainda não lê o template</span>` : ""}</div>
          <div class="sd-grid">
            <label class="sd-field"><span>Estado</span>${this.select(s, `${t}.status`, (["RASCUNHO", "EM_EDICAO", "PUBLICADO", "ARQUIVADO"] as const).map(v => ({ value: v, label: TEMPLATE_STATUS_LABELS[v] })))}</label>
            <label class="sd-field"><span>QR Code</span>${this.select(s, `${t}.qrPosition`, [{ value: "HERDAR", label: "Herdar de Relatórios" }, { value: "CENTRO", label: "Centro" }, { value: "DIREITA", label: "Direita" }])}</label>
            <label class="sd-field sd-wide"><span>Rodapé institucional (vazio = herdar)</span><input class="sd-ctl" maxlength="160" data-sec="${s}" data-path="${t}.footerInstitutionalText" data-kind="nullstr" value="${this.esc(tpl.footerInstitutionalText ?? "")}" ${this.ro(s)}></label>
          </div>
          <details><summary class="sd-note">Cores do template (desmarcado = herdar)</summary><div class="sd-color-grid">${(Object.keys(COLOR_LABELS) as Array<keyof DocumentColors>).map(k => this.optionalColor(s, `${t}.colors.${k}`, COLOR_LABELS[k])).join("")}</div></details>
        </div>`;
      }).join("")}
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 10 assinaturas

  private renderSignature(): string {
    const s: StudioSectionId = "signature";
    return `
      ${this.head("pen-line", "10 — Assinaturas Visuais", "Só a APARÊNCIA do bloco de assinatura. O Studio não executa assinatura digital — isso é de Configurações → Assinatura Digital / ICP.")}
      <div class="sd-card-block">
        ${this.row(s, "*", "Posição do bloco", `<span class="sd-tag lock"><i data-lucide="lock"></i> Fim do documento (padrão aprovado)</span>`)}
        ${this.row(s, "*", "Nome do profissional", `<span class="sd-tag lock"><i data-lucide="lock"></i> Sempre exibido</span>`)}
        ${this.row(s, "*", "Imagem da assinatura", this.toggle(s, "showImage", "Exibir"))}
        ${this.row(s, "*", "Registro profissional", this.toggle(s, "showRegistration", "Exibir"))}
        ${this.row(s, "*", "Função", this.toggle(s, "showFunction", "Exibir"))}
        ${this.row(s, "*", "Data", this.toggle(s, "showDate", "Exibir"))}
        ${this.row(s, "*", "Indicação \"assinado digitalmente\"", `<span class="sd-tag lock"><i data-lucide="lock"></i> NÃO</span> <small class="sd-note">A assinatura é a imagem cadastrada do Profissional; nenhum provedor ICP está integrado. Exibir o selo seria afirmar o que não aconteceu.</small>`)}
        ${this.row(s, "*", "QR / validação visual", `<small class="sd-note">O QR de verificação é o do cabeçalho (aba Relatórios).</small>`)}
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 11 dashboard

  private renderDashboard(): string {
    const s: StudioSectionId = "dashboard";
    return `
      ${this.head("layout-dashboard", "11 — Dashboard", "Aparência dos painéis. Fórmulas e dados dos indicadores não mudam — continuam no domínio operacional.")}
      <div class="sd-card-block">
        ${this.row(s, "cardStyle", "Visual dos cards", this.select(s, "cardStyle", [{ value: "CONTORNO", label: "Contorno" }, { value: "PLANO", label: "Plano" }, { value: "ELEVADO", label: "Elevado" }]))}
        ${this.row(s, "density", "Densidade", this.select(s, "density", [{ value: "COMPACTO", label: "Compacto" }, { value: "PADRAO", label: "Padrão" }, { value: "CONFORTAVEL", label: "Confortável" }]))}
        ${this.row(s, "indicatorStyle", "Estilo de indicadores", this.select(s, "indicatorStyle", [{ value: "NUMERO", label: "Número" }, { value: "NUMERO_E_TENDENCIA", label: "Número e tendência" }]))}
        ${this.row(s, "tableStyle", "Aparência das tabelas", this.select(s, "tableStyle", [{ value: "LISA", label: "Lisa" }, { value: "ZEBRADA", label: "Zebrada" }]))}
        ${this.row(s, "hierarchy", "Hierarquia visual", this.select(s, "hierarchy", [{ value: "NEUTRA", label: "Neutra" }, { value: "DESTAQUE", label: "Destaque" }]))}
      </div>
      <div class="sd-card-block">
        <div class="sd-card-title">Painel de Engenharia — cores neutras ${this.effect("dashboard", "engineering")}</div>
        <p class="sd-note">Aplicadas a cards, Minhas Pendências, Correções/Revisões, Atividade Recente e Ações Rápidas.</p>
        <div class="sd-color-grid">
          ${this.color(s, "engineering.accent", "Neutro (aguardando)")}
          ${this.color(s, "engineering.accentSand", "Areia (HOLD)")}
          ${this.color(s, "engineering.accentDone", "Concluído")}
          ${this.color(s, "engineering.accentWarn", "Pendência / correção")}
          ${this.color(s, "engineering.accentInfo", "Em trânsito / reenviado")}
        </div>
      </div>
      ${this.saveBar(s)}`;
  }

  // ─────────────────────────────────────────────────────── 12 preview

  private renderPreview(): string {
    const targets = ["LOGIN", "DASHBOARD", "ENGENHARIA", ...PREVIEW_DOCUMENT_TYPES.map(t => t.id), "RI", "RDO"];
    const labelOf = (id: string) => ({ LOGIN: "Login", DASHBOARD: "Dashboard", ENGENHARIA: "Painel de Engenharia", RI: "RI", RDO: "RDO" } as Record<string, string>)[id] ?? PREVIEW_DOCUMENT_TYPES.find(t => t.id === id)?.label ?? id;
    return `
      ${this.head("eye", "12 — Preview", "Veja a identidade aplicada antes de publicar. Relatórios usam um documento real e o MESMO motor do PDF: o que aparece aqui é o que sai no PDF.")}
      <div class="sd-card-block">
        <div class="sd-inline">
          <div class="sd-chips">${targets.map(t => `<button class="sd-chip ${this.previewTarget === t ? "active" : ""}" data-preview-target="${t}">${this.esc(labelOf(t))}</button>`).join("")}</div>
          <label class="sd-field"><span>Versão</span><select class="sd-ctl" id="sd-preview-source"><option value="DRAFT" ${this.previewSource === "DRAFT" ? "selected" : ""}>Rascunho</option><option value="PUBLISHED" ${this.previewSource === "PUBLISHED" ? "selected" : ""}>Publicada (V${this.view!.published.version})</option></select></label>
        </div>
      </div>
      ${this.renderPreviewBody()}`;
  }

  private renderPreviewBody(): string {
    const t = this.previewTarget;
    if (t === "LOGIN") return `<div class="sd-preview-frame">${this.loginPreview ? `<app-login-page data-preview></app-login-page>` : `<div class="sd-empty">Carregando…</div>`}</div>`;
    if (t === "DASHBOARD" || t === "ENGENHARIA") {
      const d = (this.previewSource === "DRAFT" ? this.view!.draft.design : this.view!.published.design) as Partial<VisualDesign>;
      const th = d.theme ?? THEME_CATALOG[0]!.theme;
      const eng = d.dashboard?.engineering;
      const chip = (color: string | undefined, label: string) => `<span class="sd-prev-badge" style="border-color:${this.esc(color ?? "#8C99A6")};color:${this.esc(color ?? "#8C99A6")}">${label}</span>`;
      return `<div class="sd-card-block"><p class="sd-note">Amostra com os tokens da versão escolhida (os números são ilustrativos; os reais vêm do domínio operacional).</p>
        <div class="sd-sample" style="background:${this.esc(th.background)};color:${this.esc(th.textPrimary)}">
          <div class="sd-prev-cards">${["Aguardando análise", "Em análise", "Correções", "Finalizados"].map((label, i) => `<div class="sd-sample-card" style="background:${this.esc(th.cards)};border-color:${this.esc(th.borders)}"><span style="color:${this.esc(th.textSecondary)}">${label}</span><b style="font-size:1.4rem">${[12, 4, 3, 27][i]}</b></div>`).join("")}</div>
          ${t === "ENGENHARIA" ? `<div class="sd-sample-card" style="background:${this.esc(th.cards)};border-color:${this.esc(th.borders)}"><b>Minhas Pendências</b><div class="sd-inline">${chip(eng?.accent, "Aguardando")}${chip(eng?.accentSand, "HOLD")}${chip(eng?.accentWarn, "Correção")}${chip(eng?.accentInfo, "Reenviado")}${chip(eng?.accentDone, "Finalizado")}</div></div>` : ""}
        </div></div>`;
    }
    if (t === "RI" || t === "RDO") return `<div class="sd-card-block"><div class="sd-warnline"><i data-lucide="alert-triangle"></i> O builder do ${t} ainda não lê o visual do Studio: o PDF dele continua exatamente como hoje, com ou sem publicação. Abra o documento no próprio módulo para vê-lo.</div></div>`;
    return `
      <div class="sd-card-block">
        <div class="sd-inline">
          <label class="sd-field sd-wide"><span>Documento real (${this.esc(t)})</span><select class="sd-ctl" id="sd-preview-doc"><option value="">${this.previewDocs.length === 0 ? "Clique em CARREGAR DOCUMENTOS" : "Selecione..."}</option>${this.previewDocs.map(d => `<option value="${this.esc(d.id)}" ${d.id === this.previewDocId ? "selected" : ""}>${this.esc(d.label)}</option>`).join("")}</select></label>
          <button class="sd-btn" id="sd-preview-load">CARREGAR DOCUMENTOS</button>
          <button class="sd-btn primary" id="sd-preview-render" ${this.previewDocId ? "" : "disabled"}><i data-lucide="eye"></i> VISUALIZAR RELATÓRIO</button>
        </div>
        ${this.previewMessage ? `<p class="sd-note">${this.esc(this.previewMessage)}</p>` : ""}
        <p class="sd-note">O documento real não é alterado: o preview troca só o visual numa cópia em memória.</p>
      </div>
      <div class="sd-pdf-frame"><app-pdf-preview id="sd-pdf" style="display:block;height:100%"></app-pdf-preview></div>`;
  }

  // ─────────────────────────────────────────────────────── 13 histórico

  private renderHistory(): string {
    const v = this.view!;
    return `
      ${this.head("history", "13 — Histórico Visual", "Versões publicadas (imutáveis) e o registro de cada alteração. Restaurar uma versão antiga cria uma versão NOVA — nada é apagado.")}
      <div class="sd-card-block">
        <div class="sd-card-title">Versões publicadas</div>
        <table class="sd-table"><thead><tr><th>Versão</th><th>Publicada em</th><th>Por</th><th>Motivo</th><th></th></tr></thead>
        <tbody>
          ${v.versions.map((ver, i) => `<tr><td><b>V${ver.version}</b>${i === 0 ? ` <span class="sd-tag ok">VIGENTE</span>` : ""}${ver.restoredFrom !== null ? ` <small class="sd-note">restaurada de V${ver.restoredFrom}</small>` : ""}</td><td>${this.esc(new Date(ver.publishedAt).toLocaleString("pt-BR"))}</td><td>${this.esc(ver.publishedByName)}</td><td>${this.esc(ver.reason)}</td><td>${i > 0 && v.canPublish ? `<button class="sd-btn sm ghost" data-restore="${ver.version}"><i data-lucide="rotate-ccw"></i> RESTAURAR V${ver.version}</button>` : ""}</td></tr>`).join("")}
          <tr><td><b>V0</b>${v.versions.length === 0 ? ` <span class="sd-tag ok">VIGENTE</span>` : ""}</td><td>—</td><td>Sistema</td><td>Padrão INSPECT IA (o visual de sempre)</td><td>${v.versions.length > 0 && v.canPublish ? `<button class="sd-btn sm ghost" data-restore="0"><i data-lucide="rotate-ccw"></i> RESTAURAR V0</button>` : ""}</td></tr>
        </tbody></table>
      </div>
      <div class="sd-card-block">
        <div class="sd-card-title">Alterações</div>
        ${!this.history ? `<div class="sd-empty">Carregando…</div>` : `<table class="sd-table"><thead><tr><th>Data/Hora</th><th>Usuário</th><th>Área</th><th>Ação</th><th>Detalhe</th><th>Versão</th></tr></thead>
          <tbody>${this.history.length === 0 ? `<tr><td colspan="6" class="sd-note">Nenhuma alteração registrada.</td></tr>` : this.history.map(h => `<tr><td>${this.esc(new Date(h.at).toLocaleString("pt-BR"))}</td><td>${this.esc(h.userName)}</td><td>${this.esc(h.area)}</td><td>${this.esc(HISTORY_ACTION_LABELS[h.action as keyof typeof HISTORY_ACTION_LABELS] ?? h.action)}</td><td>${this.esc(h.detail)}</td><td>${h.version === null ? "rascunho" : `V${h.version}`}</td></tr>`).join("")}</tbody></table>`}
      </div>`;
  }

  // ─────────────────────────────────────────────────────── modais

  private renderModal(): string {
    if (!this.modal || !this.view) return "";
    const v = this.view;
    if (this.modal === "publish") {
      return `<div class="sd-backdrop"><div class="sd-modal">
        <div class="sd-modal-head"><span><i data-lucide="send"></i> Publicar identidade visual — V${v.published.version + 1}</span><button class="sd-icon" data-close-modal><i data-lucide="x"></i></button></div>
        <div class="sd-modal-body">
          <p>Entram no sistema ativo: <b>${v.pending.map(s => this.esc(this.sectionLabel(s))).join(", ")}</b>.</p>
          <p class="sd-note">Documentos já aprovados não mudam — guardam o visual da época. Novos documentos e novas revisões passam a usar esta versão.</p>
          ${v.publishErrors.length > 0 ? `<div class="sd-banner error"><b>Não é possível publicar (alteração incompleta):</b><ul>${v.publishErrors.map(e => `<li>${this.esc(e)}</li>`).join("")}</ul></div>` : ""}
          ${v.warnings.length > 0 ? `<div class="sd-banner info"><b>Avisos:</b><ul>${v.warnings.map(w => `<li>${this.esc(w)}</li>`).join("")}</ul></div>` : ""}
          <label class="sd-field"><span>Motivo da publicação *</span><input class="sd-ctl" id="sd-publish-reason" value="${this.esc(this.publishReason)}" placeholder="Fica registrado na versão"></label>
        </div>
        <div class="sd-modal-foot"><button class="sd-btn ghost" data-close-modal>Cancelar</button><button class="sd-btn primary" id="sd-publish-confirm" ${v.publishErrors.length > 0 || this.publishReason.trim().length < 5 ? "disabled" : ""}>PUBLICAR V${v.published.version + 1}</button></div>
      </div></div>`;
    }
    if (this.modal === "preset" && this.presetTarget) {
      const pr = this.presetTarget;
      return `<div class="sd-backdrop"><div class="sd-modal">
        <div class="sd-modal-head"><span><i data-lucide="palette"></i> Aplicar o tema "${this.esc(pr.name)}" ao rascunho</span><button class="sd-icon" data-close-modal><i data-lucide="x"></i></button></div>
        <div class="sd-modal-body">
          <p>Reescreve no <b>RASCUNHO</b>: ${pr.sections.map(sec => this.esc(this.sectionLabel(sec))).join(", ")}.</p>
          <p class="sd-note">O sistema ativo NÃO muda agora. Depois de aplicar, confira em Preview e use PUBLICAR quando decidir. Alterações do rascunho nessas seções são substituídas pelas do tema.</p>
        </div>
        <div class="sd-modal-foot"><button class="sd-btn ghost" data-close-modal>Cancelar</button><button class="sd-btn primary" id="sd-preset-confirm">APLICAR AO RASCUNHO</button></div>
      </div></div>`;
    }
    if (this.modal === "restore") {
      const phrase = `RESTAURAR V${this.restoreTarget}`;
      return `<div class="sd-backdrop"><div class="sd-modal">
        <div class="sd-modal-head"><span><i data-lucide="rotate-ccw"></i> Restaurar V${this.restoreTarget}</span><button class="sd-icon" data-close-modal><i data-lucide="x"></i></button></div>
        <div class="sd-modal-body">
          <p>O conteúdo da V${this.restoreTarget} será publicado como <b>V${v.published.version + 1}</b>. A versão atual e todo o histórico permanecem.</p>
          ${v.pending.length > 0 ? `<div class="sd-banner error">Há alterações não publicadas no rascunho. Publique ou descarte o rascunho antes de restaurar.</div>` : ""}
          <label class="sd-field"><span>Motivo *</span><input class="sd-ctl" id="sd-restore-reason" value="${this.esc(this.restoreReason)}"></label>
          <label class="sd-field"><span>Para confirmar, digite: <b>${phrase}</b></span><input class="sd-ctl" id="sd-restore-confirm" value="${this.esc(this.restoreConfirm)}" autocomplete="off"></label>
        </div>
        <div class="sd-modal-foot"><button class="sd-btn ghost" data-close-modal>Cancelar</button><button class="sd-btn primary" id="sd-restore-go" ${this.restoreConfirm.trim().toUpperCase() === phrase && this.restoreReason.trim().length >= 5 && v.pending.length === 0 ? "" : "disabled"}>RESTAURAR</button></div>
      </div></div>`;
    }
    if (this.modal === "login") {
      return `<div class="sd-backdrop"><div class="sd-modal wide">
        <div class="sd-modal-head"><span><i data-lucide="eye"></i> VISUALIZAR LOGIN — ${this.loginModalSource === "DRAFT" ? "RASCUNHO (ainda não está no /login)" : `PUBLICADO V${v.published.version} (é exatamente o /login)`}</span><button class="sd-icon" data-close-modal><i data-lucide="x"></i></button></div>
        <div class="sd-preview-frame tall"><app-login-page data-preview></app-login-page></div>
      </div></div>`;
    }
    if (this.modal === "lightbox") {
      return `<div class="sd-backdrop"><div class="sd-modal wide">
        <div class="sd-modal-head"><span>${this.esc(this.lightbox.title)}</span><button class="sd-icon" data-close-modal><i data-lucide="x"></i></button></div>
        <div class="sd-modal-body" style="align-items:center"><img src="${this.esc(this.lightbox.src)}" alt="${this.esc(this.lightbox.title)}" style="max-width:100%;max-height:70vh;object-fit:contain;background:#fff;padding:12px;border-radius:6px"></div>
      </div></div>`;
    }
    return "";
  }

  // ═══════════════════════════════════════════════════════ eventos

  private bind() {
    this.querySelectorAll<HTMLElement>("[data-tab]").forEach(b => b.addEventListener("click", () => this.goTab(b.dataset["tab"]!)));
    const search = this.querySelector<HTMLInputElement>("#sd-search");
    search?.addEventListener("input", () => {
      this.search = search.value;
      const pos = search.selectionStart;
      this.render();
      const again = this.querySelector<HTMLInputElement>("#sd-search");
      again?.focus();
      if (pos !== null) again?.setSelectionRange(pos, pos);
    });
    this.querySelector("#sd-go-preview")?.addEventListener("click", () => this.goTab("preview"));
    this.querySelector("#sd-publish")?.addEventListener("click", () => { this.modal = "publish"; this.render(); });
    this.querySelectorAll("[data-close-modal]").forEach(b => b.addEventListener("click", () => { this.modal = null; this.render(); }));

    this.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-sec][data-path]").forEach(el => el.addEventListener("change", () => this.onField(el)));
    this.querySelectorAll<HTMLInputElement>("[data-optcolor-toggle]").forEach(el => el.addEventListener("change", () => {
      const [section, path] = el.dataset["optcolorToggle"]!.split("|") as [StudioSectionId, string];
      this.setPath(section, path, el.checked ? "#374151" : null);
      this.render();
    }));
    this.querySelectorAll<HTMLElement>("[data-save]").forEach(b => b.addEventListener("click", () => this.saveSection(b.dataset["save"] as StudioSectionId)));
    this.querySelectorAll<HTMLElement>("[data-discard-edit]").forEach(b => b.addEventListener("click", () => {
      const s = b.dataset["discardEdit"] as StudioSectionId;
      this.edits[s] = structuredClone((this.view!.draft.design as Record<string, unknown>)[s]);
      this.render();
    }));
    this.querySelector("#sd-retry")?.addEventListener("click", () => { void this.load(); });
    // temas de cor
    this.querySelectorAll<HTMLElement>("[data-theme-preview]").forEach(b => b.addEventListener("click", () => {
      this.themePreviewId = b.dataset["themePreview"] || null;
      this.render();
      this.querySelector(".sd-tp")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }));
    this.querySelector("#sd-theme-preview-close")?.addEventListener("click", () => { this.themePreviewId = null; this.render(); });
    this.querySelectorAll<HTMLElement>("[data-theme-apply]").forEach(b => b.addEventListener("click", () => this.applyTheme(b.dataset["themeApply"]!, false)));
    this.querySelectorAll<HTMLElement>("[data-theme-activate]").forEach(b => b.addEventListener("click", () => this.applyTheme(b.dataset["themeActivate"]!, true)));
    this.querySelectorAll<HTMLElement>("[data-theme-edit]").forEach(b => b.addEventListener("click", () => {
      this.themePreviewId = null;
      this.render();
      this.querySelector(".sd-theme-group")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
    this.querySelectorAll<HTMLElement>("[data-theme-reset]").forEach(b => b.addEventListener("click", () => {
      const key = b.dataset["themeReset"] as keyof ThemePalette;
      const origin = themeById((this.edits.theme as ThemeDesign).themeId);
      if (origin && THEME_COLOR_KEYS.includes(key)) { this.setPath("theme", key, origin.theme[key]); this.render(); }
    }));

    // logos
    this.querySelectorAll<HTMLElement>("[data-upload-logo]").forEach(b => b.addEventListener("click", () => this.querySelector<HTMLInputElement>(`[data-logo-input="${b.dataset["uploadLogo"]}"]`)?.click()));
    this.querySelectorAll<HTMLInputElement>("[data-logo-input]").forEach(input => input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (file) void this.uploadFile({ kind: "logo", slot: input.dataset["logoInput"] as LogoSlot }, file);
    }));
    this.querySelectorAll<HTMLElement>("[data-clear-logo]").forEach(b => b.addEventListener("click", () => this.act(async () => {
      this.setView(await this.svc.clearAsset(this.ctx(), { kind: "logo", slot: b.dataset["clearLogo"] as LogoSlot }, this.view!.draft.version));
      return "Logo restaurado para o padrão INSPECT IA no rascunho.";
    })));
    this.querySelectorAll<HTMLElement>("[data-view-src]").forEach(b => b.addEventListener("click", () => { this.lightbox = { src: b.dataset["viewSrc"]!, title: b.dataset["viewTitle"] ?? "" }; this.modal = "lightbox"; this.render(); }));
    this.querySelectorAll<HTMLImageElement>("img[data-lightbox]").forEach(img => img.addEventListener("click", () => { this.lightbox = { src: img.src, title: img.dataset["lightbox"] ?? "" }; this.modal = "lightbox"; this.render(); }));

    // temas salvos
    this.querySelectorAll<HTMLElement>("[data-apply-preset]").forEach(b => b.addEventListener("click", () => {
      this.presetTarget = this.presets.find(pr => pr.id === b.dataset["applyPreset"]) ?? null;
      this.modal = "preset";
      this.render();
    }));
    this.querySelector("#sd-preset-confirm")?.addEventListener("click", () => this.applyPreset());

    // login — logo da marca
    this.querySelector("#sd-brand-upload")?.addEventListener("click", () => this.querySelector<HTMLInputElement>("#sd-brand-input")?.click());
    const brandInput = this.querySelector<HTMLInputElement>("#sd-brand-input");
    brandInput?.addEventListener("change", () => { const f = brandInput.files?.[0]; if (f) void this.uploadFile({ kind: "loginBrandLogo" }, f); });
    this.querySelector("#sd-brand-clear")?.addEventListener("click", () => this.act(async () => {
      this.setView(await this.svc.clearAsset(this.ctx(), { kind: "loginBrandLogo" }, this.view!.draft.version));
      return "Logo da marca removido do login no rascunho.";
    }));

    // login
    this.querySelector("#sd-bg-upload")?.addEventListener("click", () => this.querySelector<HTMLInputElement>("#sd-bg-input")?.click());
    const bgInput = this.querySelector<HTMLInputElement>("#sd-bg-input");
    bgInput?.addEventListener("change", () => { const f = bgInput.files?.[0]; if (f) void this.uploadFile({ kind: "loginBackground" }, f); });
    this.querySelector("#sd-bg-clear")?.addEventListener("click", () => this.act(async () => {
      this.setView(await this.svc.clearAsset(this.ctx(), { kind: "loginBackground" }, this.view!.draft.version));
      return "Imagem de fundo restaurada para o padrão no rascunho.";
    }));
    this.querySelector("#sd-login-preview")?.addEventListener("click", () => this.act(async () => {
      this.loginPreview = await this.svc.getLoginPreview(this.ctx(), "DRAFT");
      this.loginModalSource = "DRAFT";
      this.modal = "login";
      return "";
    }, "Montando o preview do login…"));
    // PUBLICADO = o mesmo objeto que o /login recebe (getPublicLoginAppearance).
    this.querySelector("#sd-login-preview-published")?.addEventListener("click", () => this.act(async () => {
      this.loginPreview = await this.svc.getPublicLoginAppearance();
      this.loginModalSource = "PUBLISHED";
      this.modal = "login";
      return "";
    }, "Montando o preview do login publicado…"));

    // preview
    this.querySelectorAll<HTMLElement>("[data-preview-target]").forEach(b => b.addEventListener("click", async () => {
      this.previewTarget = b.dataset["previewTarget"]!;
      this.previewDocs = []; this.previewDocId = ""; this.previewMessage = "";
      await this.loadTabData();
      this.render();
    }));
    this.querySelector<HTMLSelectElement>("#sd-preview-source")?.addEventListener("change", async e => {
      this.previewSource = (e.target as HTMLSelectElement).value as "DRAFT" | "PUBLISHED";
      await this.loadTabData();
      this.render();
    });
    this.querySelector("#sd-preview-load")?.addEventListener("click", () => this.loadPreviewDocs());
    this.querySelector<HTMLSelectElement>("#sd-preview-doc")?.addEventListener("change", e => { this.previewDocId = (e.target as HTMLSelectElement).value; this.render(); });
    this.querySelector("#sd-preview-render")?.addEventListener("click", () => this.renderDocumentPreview());

    // publicação / restauração
    const reason = this.querySelector<HTMLInputElement>("#sd-publish-reason");
    reason?.addEventListener("input", () => { this.publishReason = reason.value; this.querySelector<HTMLButtonElement>("#sd-publish-confirm")?.toggleAttribute("disabled", this.publishReason.trim().length < 5 || this.view!.publishErrors.length > 0); });
    this.querySelector("#sd-publish-confirm")?.addEventListener("click", () => this.act(async () => {
      const loginIncluded = this.view!.pending.includes("login") || this.view!.pending.includes("logos");
      this.setView(await this.svc.publish(this.ctx(), this.publishReason, this.view!.draft.version));
      this.modal = null;
      this.publishReason = "";
      // Cache visual invalidado: a identidade nova vale já, sem deploy.
      await loadAndApplyAppearance();
      return loginIncluded
        ? `TELA DE LOGIN PUBLICADA COM SUCESSO — V${this.view!.published.version}. O /login já mostra este desenho.`
        : `Identidade visual publicada como V${this.view!.published.version}.`;
    }, "Publicando…"));
    this.querySelectorAll<HTMLElement>("[data-restore]").forEach(b => b.addEventListener("click", () => { this.restoreTarget = Number(b.dataset["restore"]); this.restoreReason = ""; this.restoreConfirm = ""; this.modal = "restore"; this.render(); }));
    const rr = this.querySelector<HTMLInputElement>("#sd-restore-reason");
    const rc = this.querySelector<HTMLInputElement>("#sd-restore-confirm");
    const syncRestore = () => {
      this.restoreReason = rr?.value ?? this.restoreReason;
      this.restoreConfirm = rc?.value ?? this.restoreConfirm;
      this.querySelector<HTMLButtonElement>("#sd-restore-go")?.toggleAttribute("disabled", !(this.restoreConfirm.trim().toUpperCase() === `RESTAURAR V${this.restoreTarget}` && this.restoreReason.trim().length >= 5 && this.view!.pending.length === 0));
    };
    rr?.addEventListener("input", syncRestore);
    rc?.addEventListener("input", syncRestore);
    this.querySelector("#sd-restore-go")?.addEventListener("click", () => this.act(async () => {
      this.setView(await this.svc.restoreVersion(this.ctx(), this.restoreTarget, this.restoreReason, this.restoreConfirm, this.view!.draft.version));
      this.modal = null;
      await loadAndApplyAppearance();
      return `V${this.restoreTarget} restaurada como V${this.view!.published.version}.`;
    }, "Restaurando…"));
  }

  /**
   * Tema do catálogo → RASCUNHO (só a seção Tema). Com "ativar", abre em
   * seguida a publicação: o sistema ativo só muda quando a pessoa confirma.
   */
  private async applyTheme(themeId: string, activate: boolean) {
    const entry = themeById(themeId);
    if (!entry) return;
    if (this.dirty("theme") && !window.confirm("Há cores do tema alteradas e não salvas nesta tela. Aplicar o tema descarta essas alterações. Continuar?")) return;
    await this.act(async () => {
      this.setView(await this.svc.applyTheme(this.ctx(), themeId, this.view!.draft.version));
      this.themePreviewId = null;
      if (activate) {
        this.publishReason = `Ativar o tema ${entry.name}`;
        this.publishAfterApply = true;
      }
      return activate
        ? `Tema "${entry.name}" no RASCUNHO. Confirme a publicação para ele virar o tema ativo.`
        : `Tema "${entry.name}" aplicado ao RASCUNHO. O sistema ativo não mudou — confira o preview e PUBLIQUE quando decidir.`;
    }, "Aplicando tema ao rascunho…");
    if (this.publishAfterApply) {
      this.publishAfterApply = false;
      if (this.view && this.view.pending.length > 0) { this.modal = "publish"; this.render(); }
    }
  }

  /** Busca os arquivos do tema (servidos pelo próprio sistema) e aplica ao RASCUNHO. */
  private async applyPreset() {
    const pr = this.presetTarget;
    if (!pr) return;
    await this.act(async () => {
      const assets: Record<string, string> = {};
      for (const a of pr.assets) {
        const response = await fetch(a.url);
        if (!response.ok) throw new Error(`Não foi possível ler o arquivo do tema: ${a.fileName}.`);
        const blob = await response.blob();
        assets[a.key] = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
      }
      this.setView(await this.svc.applyPreset(this.ctx(), pr.id, assets, this.view!.draft.version));
      this.modal = null;
      this.presetTarget = null;
      return `Tema "${pr.name}" aplicado ao RASCUNHO. O sistema ativo não mudou — confira em Preview e PUBLIQUE quando decidir.`;
    }, "Aplicando o tema ao rascunho…");
  }

  private setPath(section: StudioSectionId, path: string, value: unknown) {
    const parts = path.split(".");
    let t: any = this.edits[section];
    for (const p of parts.slice(0, -1)) t = t[p];
    t[parts[parts.length - 1]!] = value;
  }

  private onField(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) {
    const section = el.dataset["sec"] as StudioSectionId;
    const path = el.dataset["path"]!;
    const kind = el.dataset["kind"];
    let value: unknown = el.value;
    if (kind === "bool") value = (el as HTMLInputElement).checked;
    else if (kind === "int") value = Math.trunc(Number(el.value));
    else if (kind === "float") value = Number(el.value);
    else if (kind === "nullstr") value = el.value.trim() === "" ? null : el.value;
    else if (kind === "color") {
      const v = el.value.trim();
      if (!/^#[0-9a-fA-F]{6}$/.test(v)) { this.notice = { kind: "error", text: `Cor inválida: use #RRGGBB.` }; this.render(); return; }
      value = v.toUpperCase();
    }
    this.setPath(section, path, value);
    this.render();
  }

  private async goTab(id: string) {
    this.tab = id;
    this.notice = null;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", id);
    window.history.replaceState(null, "", url.toString());
    this.render();
    await this.loadTabData();
    this.render();
  }

  private async loadPreviewDocs() {
    await this.act(async () => {
      const t = this.previewTarget;
      const ctx = this.ctx();
      if (t === "AVR") {
        const list = await window.app.platform.avrReportService.listForEngineering(ctx);
        this.previewDocs = (list || []).slice(0, 200).map((r: any) => ({ id: r.id, label: `${r.avrNumber} — ${r.equipmentTag || ""}` }));
      } else if (t === "BOOK") {
        // BOOK — não é um catálogo de relatórios: a lista é de Equipamentos
        // (o mesmo seletor "ITEM" da tela de Book de Inspeção).
        const list = await window.app.platform.inspectionBookConsolidatorService.listItems(ctx, {});
        this.previewDocs = (list || []).slice(0, 200).map((eq: any) => ({ id: eq.id, label: `${eq.tag || eq.name} — ${eq.contextTypeLabel || ""}` }));
      } else {
        const rows = await window.app.platform.ndtReportCatalogService.search(ctx, { type: t } as never);
        this.previewDocs = (rows || []).filter((r: any) => r.type === t).slice(0, 200).map((r: any) => ({ id: r.id, label: `${r.reportNumber} — ${r.equipmentTag || ""} (${r.status})` }));
      }
      this.previewMessage = this.previewDocs.length === 0 ? `Nenhum documento ${t} visível para o seu usuário.` : `${this.previewDocs.length} documento(s).`;
      return "";
    }, "Carregando documentos…");
  }

  /** VISUALIZAR RELATÓRIO: snapshot real do serviço + visual do rascunho numa CÓPIA → o mesmo motor do PDF. */
  private async renderDocumentPreview() {
    const type = PREVIEW_DOCUMENT_TYPES.find(p => p.id === this.previewTarget);
    if (!type || !this.previewDocId) return;
    let copy: unknown = null;
    await this.act(async () => {
      const ctx = this.ctx();
      const service = (window.app.platform as any)[type.serviceKey];
      const snapshot = await service.buildDocumentSnapshot(ctx, this.previewDocId);
      const visual = await this.svc.previewDocumentVisual(ctx, type.template, this.previewSource);
      await import("../../infrastructure/documents/builders/index.js");
      copy = { ...snapshot, data: { ...(snapshot.data || {}), visual } };
      return "";
    }, "Gerando o preview (mesmo motor do PDF)…");
    // `act` redesenha a tela: o snapshot vai para o componente já recriado.
    const frame = this.querySelector("#sd-pdf") as (HTMLElement & { snapshot?: unknown }) | null;
    if (frame && copy) frame.snapshot = copy;
  }

  // ═══════════════════════════════════════════════════════ estilo

  private styles(): string {
    return `
      app-studio-design-page { display:block; padding:24px 28px; color: var(--ia-fg, #E6E9EF); }
      .sd-head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; margin-bottom:14px; flex-wrap:wrap; }
      .sd-head h1 { font-size:1.35rem; margin:0 0 4px; display:flex; align-items:center; gap:8px; letter-spacing:0.02em; }
      .sd-head p { margin:0; font-size:0.82rem; color: var(--ia-fg-soft, #A7B0C0); max-width:90ch; }
      .sd-meta { margin-top:6px !important; font-size:0.72rem !important; color: var(--ia-fg-muted, #8A94A6) !important; }
      .sd-head-actions { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
      .sd-search { display:flex; align-items:center; gap:8px; background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:6px; padding:6px 10px; min-width:260px; }
      .sd-search input { background:transparent; border:none; color: var(--ia-fg, #E6E9EF); outline:none; width:100%; font-size:0.8rem; }
      .sd-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border: 1px solid var(--ia-line, #2B3340); background: var(--ia-bg-panel, #1B212C); color: var(--ia-fg, #E6E9EF); font-size:0.76rem; font-weight:600; cursor:pointer; }
      .sd-btn.primary { background: var(--ia-bg-raised, #2B3A47); border-color: var(--ia-line, #3E5263); }
      .sd-btn.ghost { background:transparent; }
      .sd-btn.sm { padding:5px 10px; font-size:0.7rem; }
      .sd-btn[disabled] { opacity:0.45; cursor:not-allowed; }
      .sd-icon { background:none; border:none; color: var(--ia-fg-muted, #8A94A6); cursor:pointer; }
      .sd-banner { padding:10px 14px; border-radius:6px; margin-bottom:12px; font-size:0.8rem; border:1px solid; }
      .sd-banner ul { margin:6px 0 0 18px; padding:0; }
      .sd-banner.ok { background:rgba(16,185,129,0.10); border-color:rgba(16,185,129,0.35); color: var(--ia-success-fg, #a7f3d0); }
      .sd-banner.error { background:rgba(239,68,68,0.10); border-color:rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); }
      .sd-banner.info { background:rgba(125,145,170,0.10); border-color:rgba(125,145,170,0.35); color: var(--ia-fg, #C7D2E0); }
      .sd-layout { display:grid; grid-template-columns:240px 1fr; gap:18px; align-items:start; }
      .sd-nav { display:flex; flex-direction:column; gap:2px; position:sticky; top:12px; }
      .sd-nav-item { display:flex; align-items:center; gap:10px; padding:9px 12px; border-radius:6px; background:none; border:none; color: var(--ia-fg-soft, #A7B0C0); text-align:left; font-size:0.8rem; cursor:pointer; }
      .sd-nav-item:hover { background: var(--ia-bg-panel, #151A24); }
      .sd-nav-item.active { background: var(--ia-bg-panel, #1B222E); color: var(--ia-fg, #E6E9EF); font-weight:600; box-shadow: inset 2px 0 0 #7D91AA; }
      .sd-num { font-size:0.66rem; color: var(--ia-fg-muted, #8A94A6); width:18px; }
      .sd-dot { width:7px; height:7px; border-radius:50%; background:#D9A046; margin-left:auto; }
      .sd-main { min-width:0; display:flex; flex-direction:column; gap:12px; position:relative; }
      .sd-busy { position:sticky; bottom:8px; align-self:flex-end; background: var(--ia-bg-panel, #1B222E); border: 1px solid var(--ia-line, #2B3340); padding:6px 12px; border-radius:6px; font-size:0.76rem; }
      .sd-section-head h2 { font-size:1.05rem; margin:0 0 4px; display:flex; align-items:center; gap:8px; }
      .sd-section-head p { margin:0; font-size:0.8rem; color: var(--ia-fg-soft, #A7B0C0); line-height:1.5; }
      .sd-cards { display:grid; grid-template-columns:repeat(auto-fill, minmax(210px, 1fr)); gap:12px; }
      .sd-card { display:flex; flex-direction:column; gap:6px; align-items:flex-start; text-align:left; background: var(--ia-bg-panel, #131822); border: 1px solid var(--ia-line, #232A38); border-radius:10px; padding:16px; color: var(--ia-fg, #E6E9EF); cursor:pointer; }
      .sd-card:hover { border-color: var(--ia-line, #3E5263); background: var(--ia-bg-panel, #151B26); }
      .sd-card-icon { width:34px; height:34px; border-radius:8px; background: var(--ia-bg-panel, #1B222E); display:flex; align-items:center; justify-content:center; color: var(--ia-fg, #C7D2E0); }
      .sd-card-title { font-weight:700; font-size:0.86rem; }
      .sd-card-sub { font-size:0.74rem; color: var(--ia-fg-muted, #8A94A6); }
      .sd-card-block { background: var(--ia-bg-panel, #131822); border: 1px solid var(--ia-line, #232A38); border-radius:8px; padding:14px 16px; display:flex; flex-direction:column; gap:10px; font-size:0.8rem; }
      .sd-card-block .sd-card-title { text-transform:uppercase; letter-spacing:0.03em; font-size:0.78rem; color: var(--ia-fg, #C7CEDA); display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
      .sd-row { display:grid; grid-template-columns:minmax(220px, 36%) 1fr; gap:14px; padding:7px 0; border-bottom:1px solid #1C2230; }
      .sd-row:last-child { border-bottom:none; }
      .sd-row-label { display:flex; flex-direction:column; gap:4px; }
      .sd-row-control { display:flex; flex-direction:column; gap:6px; align-items:flex-start; }
      .sd-ctl { background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:6px; padding:7px 10px; color: var(--ia-fg, #E6E9EF); font-size:0.8rem; width:100%; box-sizing:border-box; font-family:inherit; }
      .sd-ctl[disabled] { opacity:0.6; }
      .sd-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(190px,1fr)); gap:10px; }
      .sd-field { display:flex; flex-direction:column; gap:4px; font-size:0.72rem; color: var(--ia-fg-soft, #A7B0C0); min-width:180px; }
      .sd-wide { grid-column: span 2; flex:1; }
      .sd-inline { display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
      .sd-toggle { display:inline-flex; align-items:center; gap:6px; cursor:pointer; }
      .sd-note { font-size:0.7rem; color: var(--ia-fg-muted, #8A94A6); line-height:1.4; display:block; }
      .sd-effect { display:inline-flex; align-items:center; gap:4px; font-size:0.62rem; font-weight:700; letter-spacing:0.04em; padding:2px 7px; border-radius:999px; width:fit-content; }
      .sd-effect.ok { background:rgba(16,185,129,0.12); color: var(--ia-success-fg, #86efac); }
      .sd-effect.reg { background:rgba(217,160,70,0.12); color: var(--ia-warning-fg, #E7C28A); }
      .sd-tag { display:inline-flex; gap:4px; align-items:center; padding:2px 8px; border-radius:999px; font-size:0.64rem; font-weight:700; background:rgba(148,163,184,0.14); color: var(--ia-fg, #cbd5e1); }
      .sd-tag.ok { background:rgba(16,185,129,0.14); color: var(--ia-success-fg, #a7f3d0); }
      .sd-tag.warn { background:rgba(217,160,70,0.14); color: var(--ia-warning-fg, #E7C28A); }
      .sd-tag.lock { background:rgba(125,145,170,0.16); color: var(--ia-fg, #C7D2E0); }
      .sd-savebar { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:10px 16px; background: var(--ia-bg-panel, #10151E); border: 1px solid var(--ia-line, #232A38); border-radius:8px; }
      .sd-savebar.ro { justify-content:flex-start; color: var(--ia-fg-muted, #8A94A6); font-size:0.78rem; gap:8px; }
      .sd-savebar-actions { display:flex; gap:8px; }
      .sd-logo-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(250px, 1fr)); gap:12px; }
      .sd-logo { background: var(--ia-bg-panel, #131822); border: 1px solid var(--ia-line, #232A38); border-radius:8px; padding:12px; display:flex; flex-direction:column; gap:8px; }
      .sd-logo-title { font-size:0.78rem; font-weight:700; display:flex; flex-direction:column; gap:4px; }
      .sd-logo-box { height:84px; background:#FFFFFF; border-radius:6px; display:flex; align-items:center; justify-content:center; padding:8px; }
      .sd-logo-box img { max-height:100%; max-width:100%; object-fit:contain; cursor:zoom-in; }
      .sd-wordmark { color:#111827; font-weight:800; letter-spacing:0.06em; font-size:1.1rem; }
      .sd-wordmark b { color: var(--ia-fg-muted, #6B7280); }
      .sd-color-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(230px, 1fr)); gap:10px; }
      .sd-load-error { display:flex; flex-direction:column; gap:8px; align-items:flex-start; padding:18px; border-radius:8px; margin-bottom:12px; border:1px solid rgba(239,68,68,0.45); background:rgba(239,68,68,0.10); color: var(--ia-danger-fg, #fca5a5); font-size:0.82rem; }
      .sd-ov-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(230px, 1fr)); gap:10px; }
      .sd-ov-item { border: 1px solid var(--ia-line, #232A38); border-radius:8px; padding:12px; display:flex; flex-direction:column; gap:4px; background: var(--ia-bg-panel, #10151E); }
      .sd-ov-label { font-size:0.66rem; letter-spacing:0.08em; text-transform:uppercase; color: var(--ia-fg-muted, #8A93A3); }
      .sd-ov-value { font-weight:700; font-size:0.9rem; color: var(--ia-fg, #E6E9EF); }
      .sd-ov-tags { display:flex; gap:6px; flex-wrap:wrap; }
      .sd-tag.err { background:rgba(239,68,68,0.16); color: var(--ia-danger-fg, #fca5a5); }
      .sd-warnline.ok { color: var(--ia-success-fg, #a7f3d0); }
      .sd-theme-cards { display:grid; grid-template-columns:repeat(auto-fill, minmax(290px, 1fr)); gap:12px; margin-bottom:12px; }
      .sd-theme-card { border: 1px solid var(--ia-line, #232A38); border-radius:10px; overflow:hidden; background: var(--ia-bg-panel, #10151E); display:flex; flex-direction:column; }
      .sd-theme-card.is-active { border-color:#3E8E6A; box-shadow:0 0 0 1px rgba(62,142,106,0.35); }
      .sd-theme-mini { height:92px; display:flex; }
      .sd-mini-side { width:22%; }
      .sd-mini-body { flex:1; padding:12px; display:flex; }
      .sd-mini-card { flex:1; border:1px solid; border-radius:6px; padding:8px; display:flex; flex-direction:column; gap:6px; }
      .sd-mini-card > span { height:6px; border-radius:3px; width:70%; }
      .sd-mini-card > span:nth-child(2) { width:45%; }
      .sd-mini-card .sd-mini-btn { width:34%; height:12px; margin-top:auto; border-radius:4px; }
      .sd-theme-card-body { padding:12px; display:flex; flex-direction:column; gap:8px; }
      .sd-theme-name { font-weight:800; font-size:0.86rem; color: var(--ia-fg, #E6E9EF); }
      .sd-swatches { display:flex; gap:4px; flex-wrap:wrap; }
      .sd-swatches span { width:20px; height:20px; border-radius:4px; border:1px solid rgba(255,255,255,0.25); }
      .sd-theme-group { display:flex; flex-direction:column; gap:6px; }
      .sd-theme-group-title { font-size:0.7rem; letter-spacing:0.06em; text-transform:uppercase; color: var(--ia-fg-muted, #8A93A3); }
      /* Preview do tema: só tokens (var(--…)), resolvidos dentro da moldura. */
      .sd-tp { display:flex; min-height:360px; border-radius:8px; overflow:hidden; border:1px solid var(--color-border-default); background:var(--color-bg-primary); color:var(--color-text-primary); font-size:0.78rem; }
      .sd-tp-side { width:150px; flex-shrink:0; background:var(--ia-sidebar-bg, #0E1216); color: var(--ia-fg, #D9DDE0); display:flex; flex-direction:column; gap:4px; padding:12px 10px; }
      .sd-tp-side b { color: var(--ia-fg, #F7F8F8); letter-spacing:0.08em; margin-bottom:8px; }
      .sd-tp-side span { padding:6px 8px; border-radius:4px; border-left:2px solid transparent; }
      .sd-tp-side span.on { background:rgba(255,255,255,0.10); color: var(--ia-fg, #FFFFFF); border-left-color:var(--color-gold-main); }
      .sd-tp-main { flex:1; display:flex; flex-direction:column; min-width:0; }
      .sd-tp-head { display:flex; justify-content:space-between; padding:10px 14px; background:var(--ia-topbar-bg, #0E1216); color: var(--ia-fg, #F7F8F8); font-weight:600; }
      .sd-tp-user { font-weight:400; color: var(--ia-fg, #D9DDE0); }
      .sd-tp-content { padding:14px; display:flex; flex-direction:column; gap:10px; }
      .sd-tp-kpis { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:8px; }
      .sd-tp-card { background:var(--color-surface-primary); border:1px solid var(--color-border-default); border-radius:6px; padding:10px; display:flex; flex-direction:column; gap:2px; }
      .sd-tp-card small { color:var(--color-text-muted); letter-spacing:0.06em; }
      .sd-tp-card strong { font-size:1.2rem; color:var(--color-text-primary); }
      .sd-tp-card span { color:var(--color-text-secondary); }
      .sd-tp-form { flex-direction:row; flex-wrap:wrap; gap:10px; align-items:flex-end; }
      .sd-tp-form label { display:flex; flex-direction:column; gap:4px; color:var(--color-text-secondary); flex:1 1 150px; }
      .sd-tp-form select, .sd-tp-form input { background:var(--color-input-bg); color:var(--color-text-primary); border:1px solid var(--color-input-border); border-radius:5px; padding:6px 8px; font:inherit; }
      .sd-tp-actions { display:flex; gap:6px; flex-wrap:wrap; }
      .sd-tp-actions button { border-radius:5px; padding:6px 10px; font:inherit; font-weight:600; cursor:default; border:1px solid var(--color-border-default); }
      .sd-tp-actions .p { background:var(--color-gold-main); color:var(--color-primary-text); border-color:var(--color-primary-active); }
      .sd-tp-actions .p:hover { background:var(--color-primary-hover); }
      .sd-tp-actions .g { background:var(--color-surface-secondary); color:var(--color-text-primary); }
      .sd-tp-actions .d { background:transparent; color:var(--status-danger-solid); border-color:var(--status-danger-border); }
      .sd-tp-table { width:100%; border-collapse:collapse; background:var(--color-surface-primary); border:1px solid var(--color-border-default); }
      .sd-tp-table th { text-align:left; padding:6px 8px; background:var(--color-primary); color:var(--color-graphite-400); font-weight:600; }
      .sd-tp-table td { padding:6px 8px; border-top:1px solid var(--color-border-default); color:var(--color-text-primary); }
      .sd-tp .b { display:inline-block; padding:1px 8px; border-radius:999px; font-size:0.66rem; font-weight:700; border:1px solid; }
      .sd-tp .b.s { color:var(--status-success-solid); background:var(--status-success-bg); border-color:var(--status-success-border); }
      .sd-tp .b.w { color:var(--status-warning-solid); background:var(--status-warning-bg); border-color:var(--status-warning-border); }
      .sd-tp .b.e { color:var(--status-danger-solid); background:var(--status-danger-bg); border-color:var(--status-danger-border); }
      .sd-tp .b.i { color:var(--status-info-solid); background:var(--status-info-bg); border-color:var(--status-info-border); }
      .sd-tp-alert { padding:8px 10px; border-radius:6px; background:var(--status-info-bg); border:1px solid var(--status-info-border); color:var(--color-text-primary); }
      .sd-tp-modal { align-self:center; width:min(320px, 100%); background:var(--color-surface-primary); border:1px solid var(--color-border-strong); border-radius:8px; padding:12px; box-shadow:0 8px 24px rgba(0,0,0,0.25); display:flex; flex-direction:column; gap:8px; }
      .sd-tp-modal-head { font-weight:700; color:var(--color-text-primary); }
      .sd-tp-modal p { margin:0; color:var(--color-text-secondary); }
      .sd-tp .muted { color:var(--color-text-muted); }
      @media (max-width: 720px) { .sd-tp { flex-direction:column; } .sd-tp-side { width:auto; flex-direction:row; flex-wrap:wrap; } .sd-tp-kpis { grid-template-columns:1fr; } }
      .sd-color { display:flex; flex-direction:column; gap:4px; font-size:0.72rem; color: var(--ia-fg-soft, #A7B0C0); }
      .sd-color-row { display:flex; gap:6px; align-items:center; }
      .sd-color input[type=color] { width:38px; height:30px; border:1px solid #2B3340; border-radius:6px; background:none; padding:0; }
      .sd-hex { max-width:100px; font-family:monospace; }
      .sd-sample { border:1px solid #2B3340; border-radius:8px; padding:14px; display:flex; flex-direction:column; gap:10px; }
      .sd-sample-card { border:1px solid; border-radius:6px; padding:10px 12px; display:flex; flex-direction:column; gap:6px; }
      .sd-sample-chips { display:flex; gap:6px; flex-wrap:wrap; }
      .sd-sample-chips span { padding:3px 8px; border-radius:999px; font-size:0.68rem; color:#111; font-weight:700; }
      .sd-prev-cards { display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:10px; }
      .sd-prev-badge { border:1px solid; border-radius:999px; padding:2px 8px; font-size:0.68rem; font-weight:700; }
      .sd-bg-thumb { max-width:260px; max-height:120px; border-radius:6px; cursor:zoom-in; }
      .sd-header-mock { background:#fff; color:#111827; border-radius:6px; padding:12px 14px; display:grid; grid-template-columns:1fr auto 1fr; gap:12px; align-items:center; font-size:0.72rem; }
      .sd-header-mock small { color: var(--ia-fg-muted, #666); }
      .sd-mock-qr { width:46px; height:46px; border:2px dashed #002B49; display:flex; align-items:center; justify-content:center; font-weight:800; color:#002B49; }
      .sd-mock-fields { color:#333; }
      .sd-chips { display:flex; gap:6px; flex-wrap:wrap; }
      .sd-chip { padding:5px 12px; border-radius:999px; border: 1px solid var(--ia-line, #2B3340); background:transparent; color: var(--ia-fg-soft, #A7B0C0); font-size:0.72rem; cursor:pointer; }
      .sd-chip.active { background: var(--ia-bg-raised, #2B3A47); border-color: var(--ia-line, #3E5263); color: var(--ia-fg, #fff); font-weight:700; }
      .sd-preview-frame { position:relative; height:560px; border: 1px solid var(--ia-line, #232A38); border-radius:8px; overflow:hidden; background: var(--ia-bg-inset, #0A0D11); }
      .sd-preview-frame.tall { height:78vh; border:none; border-radius:0; }
      .sd-pdf-frame { height:78vh; border: 1px solid var(--ia-line, #232A38); border-radius:8px; overflow:hidden; background: var(--ia-bg-panel, #0F131A); }
      .sd-table { width:100%; border-collapse:collapse; font-size:0.76rem; }
      .sd-table th { text-align:left; padding:7px 8px; border-bottom: 1px solid var(--ia-line, #2B3340); color: var(--ia-fg-muted, #8A94A6); font-size:0.64rem; text-transform:uppercase; letter-spacing:0.05em; }
      .sd-table td { padding:7px 8px; border-bottom:1px solid #1C2230; vertical-align:top; }
      .sd-warnline { font-size:0.74rem; color: var(--ia-warning-fg, #E7C28A); display:flex; gap:6px; align-items:flex-start; }
      .sd-empty { padding:30px; text-align:center; color: var(--ia-fg-muted, #8A94A6); font-size:0.82rem; }
      .sd-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.82); display:flex; align-items:center; justify-content:center; z-index:10002; padding:20px; }
      .sd-modal { background: var(--ia-bg-panel, #161A22); border: 1px solid var(--ia-line, #262C38); border-radius:12px; width:100%; max-width:620px; max-height:92vh; display:flex; flex-direction:column; overflow:hidden; }
      .sd-modal.wide { max-width:1200px; }
      .sd-modal-head { padding:12px 18px; border-bottom:1px solid #262C38; display:flex; justify-content:space-between; align-items:center; font-weight:700; font-size:0.9rem; }
      .sd-modal-body { padding:16px 18px; overflow:auto; display:flex; flex-direction:column; gap:12px; font-size:0.8rem; }
      .sd-modal-foot { padding:12px 18px; border-top:1px solid #262C38; display:flex; justify-content:flex-end; gap:8px; }
      details summary { cursor:pointer; }
      .sd-presets { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:12px; }
      .sd-preset { border: 1px solid var(--ia-line, #2B3340); border-radius:8px; overflow:hidden; background: var(--ia-bg-panel, #10151E); display:flex; flex-direction:column; }
      .sd-preset-ref { width:100%; max-height:190px; object-fit:cover; cursor:zoom-in; border-bottom:1px solid #2B3340; }
      .sd-preset-body { padding:12px; display:flex; flex-direction:column; gap:8px; }
      .sd-preset-name { font-weight:800; font-size:0.9rem; letter-spacing:0.04em; }
      .sd-preset-list { margin:0; padding-left:18px; font-size:0.74rem; color: var(--ia-fg, #C7CEDA); }
      .sd-preset-assets { display:flex; gap:8px; }
      .sd-preset-asset { background:#fff; border-radius:6px; padding:6px; height:44px; display:flex; align-items:center; }
      .sd-preset-asset img { max-height:32px; max-width:140px; object-fit:contain; }
      @media (max-width: 1100px) { .sd-layout { grid-template-columns:1fr; } .sd-nav { flex-direction:row; flex-wrap:wrap; position:static; } .sd-row { grid-template-columns:1fr; } .sd-wide { grid-column:auto; } }
    `;
  }
}

if (!customElements.get("app-studio-design-page")) {
  customElements.define("app-studio-design-page", AppStudioDesignPage);
}
